<template>
  <div class="reparaciones-kanban">
    <v-progress-linear
      v-if="loading || itemsEnProceso.length > 0"
      indeterminate
      color="info"
      class="mb-0"
    />

    <!-- Toolbar -->
    <v-row class="reparaciones-toolbar px-2 d-flex justify-space-between align-center" dense>
      <v-col cols="12" sm="6" md="4" class="py-1 mt-2">
        <v-text-field
          v-model="quickSearch"
          label="Búsqueda rápida"
          hide-details
          outlined
          dense
          clearable
          placeholder="Patente, modelo..."
        />
      </v-col>
      <v-col cols="12" sm="6" md="8" class="reparaciones-toolbar-actions py-1 mt-2 d-flex justify-end align-center flex-wrap">
        <v-chip
          v-if="unidadFiltradaId"
          small
          close
          color="purple"
          text-color="white"
          class="mr-2"
          @click:close="unidadFiltradaId = null"
        >
          Limpiar filtro
        </v-chip>
        <v-btn
          v-if="permisos.destinatarios"
          color="primary"
          small
          outlined
          class="mr-2"
          @click="abrirGestionDestinatarios"
        >
          <v-icon left small>fas fa-envelope</v-icon>
          Destinatarios alertas
        </v-btn>
        <v-btn
          color="info"
          small
          outlined
          :loading="loading"
          @click="fetchTablero"
        >
          <v-icon left small>fas fa-sync-alt</v-icon>
          Refrescar
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabs mobile -->
    <v-tabs
      v-model="mobileColumnTab"
      class="reparaciones-mobile-tabs"
      background-color="transparent"
      grow
      show-arrows
      slider-color="primary"
    >
      <v-tab
        v-for="(columna, index) in columnasFiltradas"
        :key="`mobile-tab-${columna.estado}`"
        @click="scrollToMobileColumn(columna.estado, index)"
      >
        <span>{{ columna.titulo }}</span>
        <v-chip x-small label class="ml-1">
          {{ columna.items.length }}
        </v-chip>
      </v-tab>
    </v-tabs>

    <!-- Tablero kanban (5 columnas proyectadas) -->
    <v-row class="reparaciones-board-row px-2 mt-2">
      <v-col cols="12" class="reparaciones-board-col">
        <TableroReparaciones
          ref="tableroReparaciones"
          class="reparaciones-board"
          :class="{ 'reparaciones-board--gestor-repuestos': esGestorRepuestos }"
          :columnas="columnasVisiblesKanban"
          :permisos="permisos"
          :items-en-proceso="itemsEnProceso"
          :aplicaciones-en-proceso="aplicacionesEnProceso"
          :disable-drag="true"
          @drag-change="handleDragChange"
          @agregar-concepto="handleAgregarConcepto"
          @editar-item="handleEditarItem"
          @eliminar-item="handleEliminarItem"
          @ver-comentarios="handleVerComentarios"
          @crear-aplicacion="handleCrearAplicacion"
          @completar-item="handleCompletarItem"
          @card-click="toggleFiltroUnidad"
          @ver-historial="abrirHistorial"
          @agendar="handleAgendar"
          @gestionar-turnos="abrirGestionTurnos"
          @reprogramar="handleReprogramar"
          @cancelar="handleCancelar"
          @finalizar="handleFinalizar"
          @limpiar="handleLimpiar"
          @reenviar-email="handleReenviarEmail"
          @ver-detalle-orden="handleVerDetalleOrden"
          @cargar-peritaje="handleCargarPeritaje"
          @ver-peritaje="handleVerPeritaje"
        />
      </v-col>
    </v-row>

    <!-- Datatable Finalizados -->
    <v-row v-if="!esGestorRepuestos" class="reparaciones-finalizados-row px-2 mt-4">
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon left>fas fa-check-circle</v-icon>
            Finalizados
            <v-spacer />
            <v-chip small label>{{ finalizados.length }}</v-chip>
          </v-card-title>
          <div class="reparaciones-finalizados-search px-4 pb-2">
            <SearchDataTable v-model="finalizadosSearch" placeholder="Buscar en finalizados" />
          </div>
          <v-data-table
            class="reparaciones-touch-table"
            :headers="finalizadosHeaders"
            :items="finalizadosFiltrados"
            :search="finalizadosSearch"
            :items-per-page="10"
            dense
            :no-data-text="'Sin reparaciones finalizadas'"
          >
            <template v-slot:[`item.Patente`]="{ item }">
              <strong>{{ item.Patente || '—' }}</strong>
            </template>
            <template v-slot:[`item.Empresa`]="{ item }">
              {{ nombreEmpresa(item.Empresa) }}
            </template>
            <template v-slot:[`item.FechaFinalizado`]="{ item }">
              {{ formatFecha(item.FechaFinalizado) }}
            </template>
            <template v-slot:[`item.CostoTaller`]="{ item }">
              <div class="d-flex align-center justify-end">
                <span>{{ formatCostoTaller(item.CostoTaller) }}</span>
                <v-btn
                  icon
                  x-small
                  class="ml-1"
                  aria-label="Ver detalle del costo de taller"
                  @click="abrirCostoTaller(item)"
                >
                  <v-icon x-small>fas fa-info-circle</v-icon>
                </v-btn>
              </div>
            </template>
            <template v-slot:[`item.acciones`]="{ item }">
              <v-btn x-small text color="primary" @click="abrirHistorial(item)">
                <v-icon x-small left>fas fa-eye</v-icon>
                Ver detalle
              </v-btn>
              <template v-if="puedeGestionar">
                <v-btn x-small text color="info" @click="handleReenviarEmail(item)">
                  <v-icon x-small left>fas fa-envelope</v-icon>
                  Reenviar
                </v-btn>
              </template>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Vista full-width: línea de tiempo de la reparación -->
    <v-dialog v-model="dialogoHistorial" fullscreen scrollable @input="onHistorialDialogChange">
      <v-card class="historial-dialog d-flex flex-column">
        <v-card-title class="reparaciones-dialog-title primary white--text flex-shrink-0">
          <v-icon left color="white">fas fa-history</v-icon>
          Historial de procesos
          <span v-if="historialReparacion" class="ml-2 text-subtitle-1">
            {{ historialReparacion.Patente || '—' }} · {{ historialReparacion.Marca || '' }} {{ historialReparacion.Modelo || '' }}
          </span>
          <v-chip v-if="historialTiempo" small label class="historial-header-tiempo ml-3">
            <v-icon x-small left color="white">fas fa-clock</v-icon>
            {{ historialTiempo }} en proceso
          </v-chip>
          <v-spacer />
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                dark
                aria-label="Cerrar historial"
                v-bind="attrs"
                v-on="on"
                @click="cerrarHistorial"
              >
                <v-icon>fas fa-times</v-icon>
              </v-btn>
            </template>
            <span>Cerrar historial</span>
          </v-tooltip>
        </v-card-title>
        <v-divider />

        <v-card-text class="historial-content pa-4">
          <div v-if="historialCargando" class="historial-state text-center">
            <v-progress-circular indeterminate color="primary" size="48" />
            <div class="text-body-2 mt-3">Cargando historial...</div>
          </div>

          <v-alert v-else-if="historialError" type="error" outlined class="ma-0">
            {{ historialError }}
          </v-alert>

          <div v-else-if="!historialEventos.length" class="historial-state text-center grey--text">
            <v-icon large color="grey lighten-1">fas fa-history</v-icon>
            <div class="text-body-1 mt-3">Todavía no hay eventos registrados para esta reparación.</div>
          </div>

          <div v-else class="historial-layout">
            <div class="historial-navigation">
              <v-tooltip bottom>
                <template #activator="{ on, attrs }">
                  <span class="historial-control-wrap historial-control-wrap--arriba" v-bind="attrs" v-on="on">
                    <v-btn
                      icon
                      outlined
                      color="primary"
                      class="historial-control"
                      aria-label="Desplazar hacia arriba"
                      :disabled="!historialPuedeArriba"
                      @click="desplazarHistorialVertical(-1)"
                    >
                      <v-icon small>fas fa-chevron-up</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>Desplazar hacia arriba</span>
              </v-tooltip>

              <v-tooltip right>
                <template #activator="{ on, attrs }">
                  <span class="historial-control-wrap historial-control-wrap--anterior" v-bind="attrs" v-on="on">
                    <v-btn
                      icon
                      outlined
                      color="primary"
                      class="historial-control"
                      aria-label="Ver eventos anteriores"
                      :disabled="!historialPuedeAnterior"
                      @click="desplazarHistorial(-1)"
                    >
                      <v-icon small>fas fa-chevron-left</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>Eventos anteriores</span>
              </v-tooltip>

              <div
                ref="historialTimeline"
                class="historial-timeline"
                tabindex="0"
                aria-label="Línea de tiempo de procesos de la reparación"
                @scroll.passive="actualizarControlesHistorial"
              >
                <div class="historial-track">
                  <div class="historial-lane-label historial-lane-label--arriba">
                    <v-icon x-small left>fas fa-tools</v-icon>
                    Mano de obra, turnos, órdenes y seguimiento
                  </div>
                  <div class="historial-lane-label historial-lane-label--abajo">
                    <v-icon x-small left>fas fa-box-open</v-icon>
                    <v-icon x-small left>fas fa-handshake</v-icon>
                    Repuestos y trabajos de terceros
                  </div>
                  <article
                    v-for="evento in historialEventosAgrupados"
                    :key="evento.id"
                    class="historial-evento"
                    :class="[
                      `historial-evento--${posicionEvento(evento)}`,
                      `historial-evento--${familiaEvento(evento)}`,
                    ]"
                  >
                    <div class="historial-segmento" />
                    <div class="historial-punto" :class="`historial-punto--${familiaEvento(evento)}`" />
                    <v-card
                      outlined
                      class="historial-evento-card"
                      :class="[
                        `historial-evento-card--${familiaEvento(evento)}`,
                        `historial-evento-card--${posicionEvento(evento)}`,
                      ]"
                    >
                      <v-card-title class="historial-evento-title text-subtitle-1 font-weight-bold pb-1">
                        <v-icon small left :color="colorEvento(evento)">
                          {{ iconoEvento(evento) }}
                        </v-icon>
                        {{ tituloEvento(evento) }}
                        <v-chip
                          v-if="eventoCompletado(evento)"
                          x-small
                          label
                          color="success"
                          dark
                          class="ml-2"
                        >
                          Completada
                        </v-chip>
                        <v-chip
                          v-else-if="eventoEliminado(evento)"
                          x-small
                          label
                          color="error"
                          dark
                          class="ml-2"
                        >
                          Eliminado
                        </v-chip>
                        <v-chip
                          v-else-if="eventoNuevo(evento)"
                          x-small
                          label
                          color="info"
                          dark
                          class="ml-2"
                        >
                          Nuevo
                        </v-chip>
                      </v-card-title>
                      <v-card-subtitle class="historial-evento-meta pb-2">
                        <div v-if="subtituloEvento(evento)" class="historial-evento-subtitulo">
                          {{ subtituloEvento(evento) }}
                        </div>
                        {{ formatFecha(evento.fecha) }}
                        <span v-if="tiempoTranscurrido(evento.fecha)"> · hace {{ tiempoTranscurrido(evento.fecha) }}</span>
                        <span v-if="evento.actorUsuario"> · {{ evento.actorUsuario }}</span>
                      </v-card-subtitle>
                      <v-card-text class="historial-evento-body pt-0">
                        <div v-if="alertaEnviada(evento)" class="text-body-2">
                          {{ mensajeEnvioNotificacion(evento) }}
                        </div>
                        <template v-else>
                          <div v-if="evento.origen === 'alerta'" class="mb-2">
                            <v-chip x-small label class="mr-1">{{ canalEvento(evento.canal) }}</v-chip>
                            <v-chip x-small label :color="colorResultadoEvento(evento.resultado)" dark>
                              {{ resultadoEvento(evento.resultado) }}
                            </v-chip>
                            <div v-if="evento.destinatario" class="text-caption mt-2">
                              Destinatario: {{ evento.destinatario }}
                            </div>
                          </div>
                          <dl v-if="detalleEventoItems(evento).length" class="historial-detalle mb-0">
                            <template v-for="detalle in detalleEventoItems(evento)">
                              <dt :key="`${evento.id}-${detalle.clave}-titulo`" class="text-caption font-weight-bold">
                                {{ detalle.etiqueta }}
                              </dt>
                              <dd :key="`${evento.id}-${detalle.clave}-valor`" class="text-body-2 ml-0 mb-2">
                                {{ detalle.valor }}
                              </dd>
                            </template>
                          </dl>
                        </template>
                      </v-card-text>
                    </v-card>
                  </article>
                </div>
              </div>

              <v-tooltip left>
                <template #activator="{ on, attrs }">
                  <span class="historial-control-wrap historial-control-wrap--siguiente" v-bind="attrs" v-on="on">
                    <v-btn
                      icon
                      outlined
                      color="primary"
                      class="historial-control"
                      aria-label="Ver eventos siguientes"
                      :disabled="!historialPuedeSiguiente"
                      @click="desplazarHistorial(1)"
                    >
                      <v-icon small>fas fa-chevron-right</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>Eventos siguientes</span>
              </v-tooltip>

              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <span class="historial-control-wrap historial-control-wrap--abajo" v-bind="attrs" v-on="on">
                    <v-btn
                      icon
                      outlined
                      color="primary"
                      class="historial-control"
                      aria-label="Desplazar hacia abajo"
                      :disabled="!historialPuedeAbajo"
                      @click="desplazarHistorialVertical(1)"
                    >
                      <v-icon small>fas fa-chevron-down</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>Desplazar hacia abajo</span>
              </v-tooltip>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Nueva reparación -->
    <v-dialog v-model="dialogoNuevaReparacion" max-width="700px" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable>
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline primary white--text">
          <v-icon left color="white">fas fa-plus</v-icon>
          Nueva reparación
        </v-card-title>
        <v-card-text class="pt-4">
          <v-autocomplete
            v-model="nuevaReparacion.ClaveUnidad"
            :items="unidadesSinReparacion"
            item-text="Patente"
            item-value="ClaveUnidad"
            label="Unidad recibida"
            outlined
            dense
            :filter="filtrarUnidad"
          >
            <template v-slot:item="{ item }">
              <span>{{ nombreEmpresa(item.Empresa) }} · {{ item.Patente }} — {{ item.Marca }} {{ item.Modelo }}</span>
            </template>
            <template v-slot:selection="{ item }">
              <span>{{ nombreEmpresa(item.Empresa) }} · {{ item.Patente }} — {{ item.Marca }} {{ item.Modelo }}</span>
            </template>
          </v-autocomplete>

          <v-autocomplete
            v-model="nuevaReparacion.sucursal"
            :items="sucursalesTurnoDisponibles"
            item-text="nombre"
            item-value="id"
            label="Sucursal"
            prepend-icon="fas fa-map-marker-alt"
            outlined
            dense
            class="mt-2"
            :rules="[v => !!v || 'Seleccioná la sucursal']"
          />

          <div class="concepto-form pa-2 mt-2">
            <div class="d-flex flex-wrap">
              <v-btn
                small
                :outlined="nuevoReparacionConcepto.categoria !== 'repuestos'"
                color="amber darken-2"
                class="ma-1 categoria-btn"
                :class="{ 'categoria-btn--active': nuevoReparacionConcepto.categoria === 'repuestos' }"
                @click="nuevoReparacionConcepto.categoria = 'repuestos'"
              >
                <v-icon left small>fas fa-cog</v-icon>
                Repuestos
              </v-btn>
              <v-btn
                small
                :outlined="nuevoReparacionConcepto.categoria !== 'terceros'"
                color="purple darken-2"
                class="ma-1 categoria-btn"
                :class="{ 'categoria-btn--active white--text': nuevoReparacionConcepto.categoria === 'terceros' }"
                @click="nuevoReparacionConcepto.categoria = 'terceros'"
              >
                <v-icon left small>fas fa-truck</v-icon>
                Trabajos de terceros
              </v-btn>
              <v-btn
                small
                :outlined="nuevoReparacionConcepto.categoria !== 'mano_obra'"
                color="blue darken-2"
                class="ma-1 categoria-btn"
                :class="{ 'categoria-btn--active white--text': nuevoReparacionConcepto.categoria === 'mano_obra' }"
                @click="nuevoReparacionConcepto.categoria = 'mano_obra'"
              >
                <v-icon left small>fas fa-wrench</v-icon>
                Mano de obra
              </v-btn>
            </div>
            <v-text-field
              ref="inputConceptoNuevaReparacion"
              v-model="nuevoReparacionConcepto.descripcion"
              label="Descripción de la tarea"
              outlined
              dense
              hide-details
              class="mt-2"
              maxlength="200"
              placeholder="Presioná Enter para agregar"
              @keyup.enter="agregarConceptoForm"
              append-icon="fas fa-plus"
              @click:append="agregarConceptoForm"
            />
          </div>
          <div v-if="nuevaReparacion.conceptos.length" class="mt-2">
            <div
              v-for="(concepto, idx) in nuevaReparacion.conceptos"
              :key="`nr-concepto-${idx}`"
              class="d-flex align-center item-fila ml-1"
            >
              <span class="text-caption flex-grow-1">{{ concepto.descripcion }}</span>
              <v-chip x-small :color="categoriaColor(concepto.categoria)" label class="mx-1">
                {{ categoriaLabel(concepto.categoria) }}
              </v-chip>
              <v-btn icon x-small @click="quitarConcepto(idx)">
                <v-icon x-small>fas fa-times</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey darken-1" :disabled="creando" @click="dialogoNuevaReparacion = false">
            Cancelar
          </v-btn>
          <v-btn color="primary" :loading="creando" @click="confirmarNuevaReparacion">
            <v-icon left small>fas fa-check</v-icon>
            Crear
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: gestión flexible de turnos de la unidad -->
    <v-dialog v-if="!esGestorRepuestos" v-model="dialogoAgendar" max-width="560px" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable>
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline primary white--text">
          <v-icon left color="white">fas fa-calendar-plus</v-icon>
          Gestionar turnos
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-2">
            <strong>{{ reparacionAAgendar?.Patente || "—" }}</strong>
            {{ reparacionAAgendar ? `(${reparacionAAgendar.Marca || ""} ${reparacionAAgendar.Modelo || ""})` : "" }}
          </p>
          <div class="text-subtitle-2 mb-2">Turnos existentes</div>
          <v-alert v-if="!turnosGestionados.length" dense text type="info">
            Esta unidad todavía no tiene turnos.
          </v-alert>
          <v-list v-else dense outlined class="mb-3">
            <v-list-item v-for="turno in turnosGestionados" :key="turno.Id">
              <v-list-item-icon><v-icon color="primary">fas fa-calendar-check</v-icon></v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ formatTurno(turno) }}</v-list-item-title>
              </v-list-item-content>

            </v-list-item>
          </v-list>

           <div class="text-subtitle-2 mb-2">
             {{ reprogramando ? "Reprogramar turno seleccionado" : "Crear nuevo turno" }}
           </div>
            <div v-if="!reprogramando && chipsTurno.length" class="mb-3">
             <div class="text-body-2 mb-1">Tareas de la unidad que se aplicarán en el turno</div>
             <div class="text-caption grey--text text--darken-1 mb-2">
               Seleccioná una o dos tareas para generar el incidente del cliente.
             </div>
             <v-chip
               v-for="chip in chipsTurno"
               :key="chip.key"
               :color="chipSeleccionadoTurno(chip) ? 'primary' : (chip.tipo === 'repuestos' ? 'green lighten-4' : 'info lighten-4')"
               :text-color="chipSeleccionadoTurno(chip) ? 'white' : (chip.tipo === 'repuestos' ? 'green darken-2' : 'info darken-1')"
               small
               class="mr-1 mb-1 py-2"
               :outlined="!chipSeleccionadoTurno(chip)"
               @click="toggleChipTurno(chip)"
             >
               <v-icon left x-small>{{ chipSeleccionadoTurno(chip) ? 'fas fa-check' : (chip.tipo === 'repuestos' ? 'fas fa-boxes' : 'fas fa-wrench') }}</v-icon>
               {{ chip.label }}
             </v-chip>
             <div v-if="!seleccionTurnoValida" class="text-caption orange--text text--darken-2 mt-1">
               Seleccioná al menos una tarea para poder agendar.
             </div>
           </div>
          <v-alert v-else-if="!reprogramando && !chipsTurno.length" dense text type="info" class="mb-3">
            No hay repuestos confirmados ni tareas de mano de obra para agendar.
          </v-alert>
          <v-autocomplete
             v-model="agendarForm.sucursal"
             :items="sucursalesTurnoDisponibles"
             item-text="nombre"
             item-value="id"
             label="Sucursal"
             prepend-icon="fas fa-map-marker-alt"
             :disabled="estaScopeadoPorSucursal"
             outlined
             dense
             class="mb-3"
           />
           <v-row dense>
             <v-col cols="6">
               <v-autocomplete
                 v-model="agendarForm.recepcionistaId"
                 :items="recepcionistas"
                 :loading="cargandoRecepcionistas"
                 :disabled="!agendarForm.sucursal || cargandoRecepcionistas"
                 item-text="nombre"
                 item-value="id"
                 label="Recepcionista"
                 prepend-icon="fas fa-user-tie"
                 no-data-text="No hay recepcionistas activos para esta sucursal"
                 outlined
                 dense
               />
             </v-col>
             <v-col cols="6">
               <v-text-field
                 v-model.number="agendarForm.tiempoEstimado"
                 type="number"
                 min="0.25"
                 step="0.25"
                 label="Tiempo estimado (horas)"
                 hint="Ejemplo: 1,5 horas"
                 persistent-hint
                 :rules="[reglaTiempoManoObra]"
                 prepend-icon="fas fa-hourglass-half"
                 outlined
                 dense
               />
             </v-col>
           </v-row>
            <v-row dense>
              <v-col cols="6">
                <v-menu
                  ref="menuFecha"
                  v-model="menuFecha"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="fechaFormateada"
                      :rules="[v => reglaFechaTurno(agendarForm.fecha)]"
                      label="Fecha"
                      prepend-icon="fas fa-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="agendarForm.fecha"
                    :min="fechaMinimaTurno"
                    no-title
                    @input="menuFecha = false"
                  />
                </v-menu>
              </v-col>
              <v-col cols="6">
                <v-autocomplete
                  class="mt-3"
                  v-model="agendarForm.hora"
                  :items="horariosDisponiblesTurno"
                  :rules="[reglaHoraTurno]"
                  :loading="cargandoDisponibilidad"
                  :disabled="!disponibilidadGestionCompleta || cargandoDisponibilidad"
                  label="Horario disponible"
                  no-data-text="No hay turnos disponibles para la fecha seleccionada"
                  prepend-icon="fas fa-clock"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey darken-1" :disabled="agendando" @click="dialogoAgendar = false">
            Cancelar
          </v-btn>
          <v-btn color="primary" :loading="agendando" :disabled="!puedeConfirmarTurno" @click="confirmarAgendar">
            <v-icon left small>fas fa-check</v-icon>
            {{ reprogramando ? "Reprogramar" : "Crear turno" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Confirmar finalización -->
    <v-dialog v-model="dialogoConfirmarFinalizar" max-width="400px">
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline green white--text">
          <v-icon left color="white">fas fa-check-circle</v-icon>
          Finalizar reparación
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-1">¿Desea finalizar la reparación de esta unidad?</p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey darken-1" :disabled="finalizando" @click="cancelarFinalizacion">
            Cancelar
          </v-btn>
          <v-btn color="green" :loading="finalizando" @click="confirmarFinalizacion">
            <v-icon left small>fas fa-check</v-icon>
            Finalizar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Confirmar agendamiento tras repuestos completos -->
    <v-dialog v-if="!esGestorRepuestos" v-model="dialogoAgendarPrompt" max-width="400px">
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline primary white--text">
          <v-icon left color="white">fas fa-calendar-plus</v-icon>
          Agendar turno
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-1">
            Los repuestos están completos. ¿Desea agendar un turno para aplicar los repuestos?
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey darken-1" @click="dialogoAgendarPrompt = false">
            Ahora no
          </v-btn>
          <v-btn color="primary" @click="confirmarAgendarPrompt">
            <v-icon left small>fas fa-check</v-icon>
            Agendar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Confirmar cancelación de turno -->
    <v-dialog v-model="dialogoConfirmarCancelar" max-width="400px">
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline orange white--text">
          <v-icon left color="white">fas fa-calendar-times</v-icon>
          Cancelar turno
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-1">
            ¿Estás seguro de que querés cancelar el turno de
            <strong>{{ reparacionACancelar?.Patente || "—" }}</strong>?
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey darken-1" :disabled="cancelando" @click="dialogoConfirmarCancelar = false">
            Volver
          </v-btn>
          <v-btn color="orange" :loading="cancelando" @click="confirmarCancelar">
            <v-icon left small>fas fa-check</v-icon>
            Cancelar turno
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Confirmar limpieza -->
    <v-dialog v-model="dialogoConfirmarLimpiar" max-width="400px">
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline grey white--text">
          <v-icon left color="white">fas fa-trash</v-icon>
          Limpiar reparación
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-1">
            ¿Estás seguro de que querés limpiar la reparación de
            <strong>{{ reparacionALimpiar?.Patente || "—" }}</strong>?
            Se eliminará del tablero pero el historial se conserva.
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey darken-1" :disabled="limpiando" @click="dialogoConfirmarLimpiar = false">
            Cancelar
          </v-btn>
          <v-btn color="grey" :loading="limpiando" @click="confirmarLimpiar">
            <v-icon left small>fas fa-trash</v-icon>
            Limpiar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: prompt de texto (descripción de concepto) -->
    <v-dialog v-model="dialogoTexto" max-width="400px">
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline primary white--text">
          {{ dialogoTextoTitulo }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="dialogoTextoValor"
            label="Descripción"
            outlined
            dense
            autofocus
            maxlength="200"
            @keyup.enter="confirmarDialogoTexto"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey darken-1" @click="dialogoTexto = false">Cancelar</v-btn>
          <v-btn color="primary" @click="confirmarDialogoTexto">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: nuevo ítem (categoría con botones + descripción) -->
    <v-dialog v-model="dialogoItem" max-width="400px">
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline primary white--text">
          {{ dialogoItemTitulo }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-autocomplete
            v-model="dialogoItemSucursal"
            :items="sucursalesTurnoDisponibles"
            item-text="nombre"
            item-value="id"
            label="Sucursal"
            prepend-icon="fas fa-map-marker-alt"
            :rules="[v => !!v || 'Seleccioná la sucursal']"
            outlined
            dense
            class="mb-2"
          />
          <div class="d-flex flex-wrap">
            <v-btn
              small
              :outlined="dialogoItemCategoria !== 'repuestos'"
              color="amber darken-2"
              class="ma-1 categoria-btn"
              :class="{ 'categoria-btn--active': dialogoItemCategoria === 'repuestos' }"
              @click="dialogoItemCategoria = 'repuestos'"
            >
              <v-icon left small>fas fa-cog</v-icon>
              Repuestos
            </v-btn>
            <v-btn
              small
              :outlined="dialogoItemCategoria !== 'terceros'"
              color="purple darken-2"
              class="ma-1 categoria-btn"
              :class="{ 'categoria-btn--active white--text': dialogoItemCategoria === 'terceros' }"
              @click="dialogoItemCategoria = 'terceros'"
            >
              <v-icon left small>fas fa-truck</v-icon>
              Trabajos de terceros
            </v-btn>
            <v-btn
              small
              :outlined="dialogoItemCategoria !== 'mano_obra'"
              color="blue darken-2"
              class="ma-1 categoria-btn"
              :class="{ 'categoria-btn--active white--text': dialogoItemCategoria === 'mano_obra' }"
              @click="dialogoItemCategoria = 'mano_obra'"
            >
              <v-icon left small>fas fa-wrench</v-icon>
              Mano de obra
            </v-btn>
          </div>
          <div class="d-flex align-center flex-wrap mt-2">
            <v-text-field
              ref="inputConceptoDialogoItem"
              v-model="dialogoItemValor"
              label="Descripción de la tarea"
              outlined
              dense
              autofocus
              maxlength="200"
              hide-details
              class="flex-grow-1"
              placeholder="Presioná Enter para agregar"
              @keyup.enter="agregarConceptoDialogoItem"
              append-icon="fas fa-plus"
              @click:append="agregarConceptoDialogoItem"
            />
            <v-checkbox
              v-if="dialogoItemCategoria === 'mano_obra'"
              v-model="dialogoItemNotificar"
              dense
              hide-details
              class="ml-3 mt-0 pt-0 flex-shrink-0"
            >
              <template v-slot:label>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small v-bind="attrs" v-on="on">fas fa-bell</v-icon>
                  </template>
                  <span>Enviar notificación</span>
                </v-tooltip>
              </template>
            </v-checkbox>
          </div>
          <div v-if="dialogoItemConceptos.length" class="mt-2">
            <div
              v-for="(concepto, idx) in dialogoItemConceptos"
              :key="`di-concepto-${idx}`"
              class="d-flex align-center item-fila ml-1"
            >
              <span class="text-caption flex-grow-1">{{ concepto.descripcion }}</span>
              <v-chip x-small :color="categoriaColor(concepto.categoria)" label class="mx-1">
                {{ categoriaLabel(concepto.categoria) }}
              </v-chip>
              <v-btn icon x-small @click="quitarConceptoDialogoItem(idx)">
                <v-icon x-small>fas fa-times</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey darken-1" @click="dialogoItem = false">Cancelar</v-btn>
          <v-btn color="primary" @click="confirmarDialogoItem">
            Guardar<span v-if="dialogoItemConceptos.length"> ({{ dialogoItemConceptos.length }})</span>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: comentarios del ítem -->
    <v-dialog v-model="dialogoComentariosItem" max-width="560px" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable>
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline primary white--text">
          <v-icon left color="white">fas fa-comment</v-icon>
          Comentarios
          <span v-if="comentariosItemCtx?.descripcion" class="ml-2 text-subtitle-1">
            {{ comentariosItemCtx.descripcion }}
          </span>
          <v-spacer />
          <v-btn icon dark aria-label="Cerrar" @click="dialogoComentariosItem = false">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div v-if="comentariosCargando" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <v-alert v-else-if="comentariosError" type="error" outlined class="ma-0">
            {{ comentariosError }}
          </v-alert>
          <div v-else-if="!comentariosLista.length" class="text-center grey--text py-6">
            Sin comentarios todavía
          </div>
          <v-list v-else dense class="comentarios-lista">
            <v-list-item v-for="comentario in comentariosLista" :key="comentario.Id">
              <v-list-item-avatar size="32">
                <v-icon small>fas fa-user</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="text-subtitle-2">
                  {{ comentario.UsuarioNombre }}
                  <span class="caption grey--text ml-1">{{ formatFecha(comentario.FechaAlta) }}</span>
                </v-list-item-title>
                <v-list-item-subtitle class="text-wrap">{{ comentario.Comentario }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions pa-3">
          <v-text-field
            v-model="comentariosNuevo"
            label="Nuevo comentario"
            outlined
            dense
            maxlength="1000"
            hide-details
            append-icon="fas fa-paper-plane"
            :disabled="agregandoComentario"
            class="mr-2"
            @keyup.enter="agregarComentarioItem"
            @click:append="agregarComentarioItem"
          />
          <v-btn
            color="primary"
            :loading="agregandoComentario"
            :disabled="!comentariosNuevo.trim()"
            @click="agregarComentarioItem"
          >
            Comentar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Ver detalle (finalizados) -->
    <v-dialog v-model="dialogoDetalle" max-width="560px" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable>
      <v-card v-if="detalleReparacion">
        <v-card-title class="reparaciones-dialog-title headline">
          <v-icon left>fas fa-car</v-icon>
          {{ detalleReparacion.Patente || "—" }}
          <v-spacer />
          <v-chip x-small label color="green" class="white--text">Finalizado</v-chip>
        </v-card-title>
        <v-card-text class="pt-2">
          <div class="text-caption grey--text">
            {{ detalleReparacion.Marca }} {{ detalleReparacion.Modelo }}
            <template v-if="detalleReparacion.Anio">— {{ detalleReparacion.Anio }}</template>
            <template v-if="detalleReparacion.Color">— {{ detalleReparacion.Color }}</template>
          </div>
          <div v-if="detalleReparacion.FechaFinalizado" class="text-caption mt-1">
            Finalizado: {{ formatFecha(detalleReparacion.FechaFinalizado) }} por {{ detalleReparacion.Responsable || "—" }}
          </div>
          <div class="conceptos-detalle mt-2">
            <div
              v-for="grupo in gruposDetalle()"
              :key="`detalle-${grupo.categoria}`"
              class="mb-2"
            >
              <div class="text-caption font-weight-bold">{{ grupo.label }}</div>
              <div
                v-for="it in grupo.items"
                :key="it.Id"
                class="text-caption ml-2 d-flex align-center"
              >
                <v-icon x-small :color="it.Completado ? 'green' : 'grey'" class="mr-1">
                  {{ it.Completado ? "fas fa-check-circle" : "fas fa-circle" }}
                </v-icon>
                <span>{{ it.Descripcion }}</span>
                <v-chip x-small label class="ml-1">{{ categoriaLabel(it.Categoria) }}</v-chip>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey" @click="dialogoDetalle = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: detalle del costo de taller (finalizados) -->
    <v-dialog v-model="dialogoCostoTaller" max-width="860px" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable @input="onCostoTallerDialogChange">
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline primary white--text">
          <v-icon left color="white">fas fa-info-circle</v-icon>
          Costo de taller
          <span v-if="costoTallerUnidad" class="ml-2 text-subtitle-1">
            {{ costoTallerUnidad.Patente || '—' }}
          </span>
          <v-spacer />
          <v-btn icon dark aria-label="Cerrar" @click="dialogoCostoTaller = false">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div v-if="costoTallerCargando" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <v-alert v-else-if="costoTallerError" type="error" outlined class="ma-0">
            {{ costoTallerError }}
          </v-alert>
          <template v-else>
            <div v-if="!costoTallerLineas.length" class="text-center grey--text py-6">
              La unidad no registra cargos de taller.
            </div>
            <template v-else>
              <v-row dense class="mb-1">
                <v-col cols="12" sm="4">
                  <FechaPicker
                    v-model="costoTallerFiltroDesde"
                    label="Cierre desde"
                    :clearable="true"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <FechaPicker
                    v-model="costoTallerFiltroHasta"
                    label="Cierre hasta"
                    :clearable="true"
                  />
                </v-col>
                <v-col cols="12" sm="4" class="d-flex align-center">
                  <v-btn
                    v-if="costoTallerFiltroActivo"
                    text
                    small
                    color="primary"
                    @click="limpiarFiltroCostoTaller"
                  >
                    <v-icon left small>fas fa-times</v-icon>
                    Limpiar
                  </v-btn>
                  <v-spacer />
                  <span v-if="costoTallerSinCierre" class="text-caption grey--text text-right">
                    {{ costoTallerSinCierre }} cargo{{ costoTallerSinCierre === 1 ? '' : 's' }} sin cierre fuera del rango
                  </span>
                </v-col>
              </v-row>
              <div v-if="!costoTallerLineasVisibles.length" class="text-center grey--text py-6">
                No hay cargos de taller en el rango de cierre seleccionado.
              </div>
              <template v-else>
                <v-data-table
                  class="reparaciones-touch-table"
                  :headers="costoTallerHeaders"
                  :items="costoTallerLineasVisibles"
                  dense
                  :items-per-page="-1"
                  disable-pagination
                  hide-default-footer
                >
                  <template v-slot:[`item.Cierre`]="{ item }">{{ item.Cierre || '—' }}</template>
                  <template v-slot:[`item.Precio`]="{ item }">{{ formatMoney(item.Precio) }}</template>
                  <template v-slot:[`item.Monto`]="{ item }">{{ formatMoney(item.Monto) }}</template>
                </v-data-table>
                <div class="d-flex justify-end align-center mt-3">
                  <span class="text-subtitle-2 mr-2">Total ({{ costoTallerLineasVisibles.length }} cargos):</span>
                  <span class="text-h6">{{ formatMoney(costoTallerTotalVisible) }}</span>
                </div>
              </template>
            </template>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Ver detalle de órdenes de servicio -->
    <v-dialog v-model="dialogoDetalleOrden" max-width="640px" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable>
      <v-card v-if="detalleOrdenUnidad">
        <v-card-title class="reparaciones-dialog-title reparaciones-dialog-title--closable headline primary white--text">
          <v-icon left color="white">fas fa-wrench</v-icon>
          <span class="reparaciones-dialog-title-text">
            Órdenes de servicio
            <span class="text-body-2 ml-2">{{ detalleOrdenUnidad.Patente || '—' }}</span>
            <span v-if="detalleOrdenUnidad.Marca || detalleOrdenUnidad.Modelo" class="text-body-2 ml-1">
              · {{ detalleOrdenUnidad.Marca }} {{ detalleOrdenUnidad.Modelo }}
            </span>
          </span>
          <v-spacer />
          <v-btn icon dark aria-label="Cerrar" @click="dialogoDetalleOrden = false">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="text-subtitle-2 mb-2">Órdenes de servicio</div>
          <div v-if="!ordenesDetalle.length" class="text-body-2 grey--text mb-2">
            La unidad no tiene órdenes de servicio abiertas.
          </div>
          <v-card
            v-for="orden in ordenesDetalle"
            :key="`orden-${orden.OrdenId}`"
            outlined
            class="mb-2"
          >
            <v-card-text class="py-3">
              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">Fecha de apertura</div>
                <div class="text-body-2">{{ formatFecha(orden.Apertura) }}</div>
              </div>
              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">Número de orden</div>
                <div class="text-body-2">{{ orden.NumeroOrden || "—" }}</div>
              </div>
              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">Incidentes del cliente</div>
                <div class="text-body-2">{{ orden.IncidentesCliente || "—" }}</div>
              </div>
              <div>
                <div class="text-caption grey--text text--darken-1">Resultado técnico</div>
                <div class="text-body-2">{{ orden.ResultadoTecnico || "—" }}</div>
              </div>
            </v-card-text>
          </v-card>

          <div class="text-subtitle-2 mt-4 mb-2">Órdenes anteriores</div>
          <div
            v-if="ordenesAnterioresTotalDetalle > ordenesAnterioresDetalle.length"
            class="text-caption grey--text mb-2"
          >
            Mostrando las {{ ordenesAnterioresDetalle.length }} más recientes de {{ ordenesAnterioresTotalDetalle }} órdenes cerradas.
          </div>
          <div v-if="!ordenesAnterioresDetalle.length" class="text-body-2 grey--text">
            No hay órdenes anteriores registradas.
          </div>
          <v-card
            v-for="orden in ordenesAnterioresDetalle"
            :key="`orden-anterior-${orden.OrdenId}`"
            outlined
            class="mb-2"
          >
            <v-card-text class="py-3">
              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">Fecha de cierre</div>
                <div class="text-body-2">{{ formatFecha(orden.Cierre) }}</div>
              </div>
              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">Fecha de apertura</div>
                <div class="text-body-2">{{ formatFecha(orden.Apertura) }}</div>
              </div>
              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">Número de orden</div>
                <div class="text-body-2">{{ orden.NumeroOrden || "—" }}</div>
              </div>
              <div class="mb-2">
                <div class="text-caption grey--text text--darken-1">Incidentes del cliente</div>
                <div class="text-body-2">{{ orden.IncidentesCliente || "—" }}</div>
              </div>
              <div>
                <div class="text-caption grey--text text--darken-1">Resultado técnico</div>
                <div class="text-body-2">{{ orden.ResultadoTecnico || "—" }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey" @click="dialogoDetalleOrden = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: ABM de destinatarios de alertas -->
    <v-dialog v-model="dialogoDestinatarios" max-width="840" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable>
      <v-card>
        <v-card-title class="reparaciones-dialog-title reparaciones-dialog-title--closable pb-2">
          <span class="reparaciones-dialog-title-text">Destinatarios de alertas de reparación</span>
          <v-spacer />
          <v-btn icon class="ml-2" aria-label="Cerrar" @click="dialogoDestinatarios = false">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-6">
          <v-alert dense outlined type="info" class="mb-4">
            Gestioná listas separadas por sucursal y tipo de alerta. Cada destinatario recibe solo el aviso del tipo seleccionado.
          </v-alert>
          <v-row dense>
            <v-col cols="12" sm="3">
              <v-autocomplete
                v-model="destinatarioSucursalNumero"
                :items="sucursalesAgendables"
                item-value="id"
                item-text="provincia"
                label="Sucursal *"
                dense
                outlined
                hide-details
                @change="cargarDestinatarios"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-autocomplete
                v-model="destinatarioTipoAlerta"
                :items="tiposDestinatarioAlertaOptions"
                item-value="value"
                item-text="text"
                label="Tipo de alerta *"
                dense
                outlined
                hide-details
                @change="cargarDestinatarios"
              />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model.trim="destinatarioNuevoNombre"
                label="Nombre *"
                dense
                outlined
                hide-details
                maxlength="100"
              />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model.trim="destinatarioNuevoEmail"
                label="Email *"
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                v-model="destinatarioNuevoTelefono"
                v-mask="'###############'"
                label="Teléfono"
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="12" sm="1" class="d-flex align-center justify-end">
              <v-btn color="success" small block :loading="guardandoDestinatario" @click="crearDestinatario">
                Alta
              </v-btn>
            </v-col>

            <v-col cols="12" class="mt-4">
              <v-data-table
                class="reparaciones-touch-table"
                :headers="headersDestinatarios"
                :items="destinatariosFiltrados"
                dense
                :items-per-page="8"
                :loading="cargandoDestinatarios"
              >
                <template #[`item.activo`]="{ item }">
                  <v-chip x-small :color="Number(item.activo) === 0 ? 'warning' : 'success'" dark>
                    {{ Number(item.activo) === 0 ? 'INACTIVO' : 'ACTIVO' }}
                  </v-chip>
                </template>
                <template #[`item.tipo_alerta`]="{ item }">
                  {{ tipoAlertaDestinatarioTexto(item.tipo_alerta) }}
                </template>
                <template #[`item.nombre`]="{ item }">
                  {{ item.nombre || '—' }}
                </template>
                <template #[`item.telefono`]="{ item }">
                  {{ formatearTelefono(item.telefono) || '—' }}
                </template>
                <template #[`item.sucursal_numero`]="{ item }">
                  {{ sucursalDestinatarioTexto(item.sucursal_numero) }}
                </template>
                <template #[`item.acciones`]="{ item }">
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn x-small icon color="info" v-bind="attrs" v-on="on" @click="abrirEditarDestinatario(item)">
                        <v-icon small>fas fa-edit</v-icon>
                      </v-btn>
                    </template>
                    <span>Editar</span>
                  </v-tooltip>

                  <v-tooltip v-if="Number(item.activo) !== 0" bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        x-small
                        icon
                        color="warning"
                        v-bind="attrs"
                        v-on="on"
                        :loading="destinatarioBajandoId === item.id"
                        @click="darBajaDestinatario(item)"
                      >
                        <v-icon small>fas fa-ban</v-icon>
                      </v-btn>
                    </template>
                    <span>Dar de baja</span>
                  </v-tooltip>

                  <v-tooltip v-else bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        x-small
                        icon
                        color="success"
                        v-bind="attrs"
                        v-on="on"
                        :loading="destinatarioAltaId === item.id"
                        @click="darAltaDestinatario(item)"
                      >
                        <v-icon small>fas fa-check</v-icon>
                      </v-btn>
                    </template>
                    <span>Dar de alta</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Diálogo: editar destinatario de alertas -->
    <v-dialog v-model="dialogoEditarDestinatario" max-width="560" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable>
      <v-card>
        <v-card-title class="reparaciones-dialog-title reparaciones-dialog-title--closable pb-2">
          <span class="reparaciones-dialog-title-text">Editar destinatario de alertas de reparación</span>
          <v-spacer />
          <v-btn icon class="ml-2" aria-label="Cerrar" @click="cerrarEditarDestinatario">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-5">
          <v-row dense>
            <v-col cols="12">
              <v-autocomplete
                v-model="destinatarioEditar.sucursal_numero"
                :items="sucursalesAgendables"
                item-value="id"
                item-text="provincia"
                label="Sucursal *"
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="12">
              <v-autocomplete
                v-model="destinatarioEditar.tipo_alerta"
                :items="tiposDestinatarioAlertaOptions"
                item-value="value"
                item-text="text"
                label="Tipo de alerta *"
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model.trim="destinatarioEditar.nombre"
                label="Nombre *"
                dense
                outlined
                hide-details
                maxlength="100"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model.trim="destinatarioEditar.email"
                label="Email *"
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="destinatarioEditar.telefono"
                v-mask="'###############'"
                label="Teléfono"
                dense
                outlined
                hide-details
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text @click="cerrarEditarDestinatario">Cancelar</v-btn>
          <v-btn color="primary" :loading="guardandoEdicionDestinatario" @click="guardarEdicionDestinatario">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: confirmar eliminación -->
    <v-dialog v-model="dialogoConfirmarEliminar" max-width="400px">
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline warning white--text">
          <v-icon left color="white">fas fa-exclamation-triangle</v-icon>
          Confirmar eliminación
        </v-card-title>
        <v-card-text class="pt-4 text-body-1">
          {{ dialogoConfirmarEliminarMensaje }}
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey darken-1" :disabled="eliminandoItem" @click="dialogoConfirmarEliminar = false">
            Cancelar
          </v-btn>
          <v-btn color="error" :loading="eliminandoItem" @click="confirmarEliminacion">
            <v-icon left small>fas fa-trash</v-icon>
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Cargar peritaje -->
    <v-dialog v-model="dialogoPeritaje" max-width="600px" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline teal white--text">
          <v-icon left color="white">fas fa-file-upload</v-icon>
          Cargar peritaje
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="text-body-2 mb-3">
            <strong>{{ peritajeUnidad?.Patente || '—' }}</strong>
            {{ peritajeUnidad ? `(${peritajeUnidad.Marca || ''} ${peritajeUnidad.Modelo || ''})` : '' }}
          </p>
          <v-alert v-if="peritajeError" dense text type="error" class="mb-3">
            {{ peritajeError }}
          </v-alert>
          <FileDropzone
            :files.sync="peritajeArchivos"
            :tipos="peritajeTiposPermitidos"
            :multiple="false"
            :max_size="10"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions justify-end">
          <v-btn text color="grey darken-1" :disabled="subiendoPeritaje" @click="dialogoPeritaje = false">
            Cancelar
          </v-btn>
          <v-btn
            color="teal"
            :loading="subiendoPeritaje"
            :disabled="!peritajeArchivos.length"
            @click="confirmarSubirPeritaje"
          >
            <v-icon left small>fas fa-upload</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Ver peritaje -->
    <v-dialog v-model="dialogoVerPeritaje" max-width="900px" :fullscreen="$vuetify.breakpoint.xsOnly" scrollable>
      <v-card>
        <v-card-title class="reparaciones-dialog-title headline info white--text">
          <v-icon left color="white">fas fa-eye</v-icon>
          Peritaje — {{ verPeritajeUnidad?.Patente || '—' }}
          <v-spacer />
          <v-btn icon dark @click="dialogoVerPeritaje = false">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <div v-if="verPeritajeCargando" class="text-center py-8">
            <v-progress-circular indeterminate color="info" />
            <div class="text-caption mt-2">Cargando peritaje...</div>
          </div>
          <div v-else-if="verPeritajeURL" class="peritaje-viewer-container">
            <!-- El diálogo principal ya contiene el visor; no abrir otro diálogo anidado. -->
            <iframe
              v-if="esPeritajePDF"
              :key="verPeritajeURL"
              class="peritaje-pdf-frame"
              :src="verPeritajeURL"
              :title="`Peritaje - ${verPeritajeUnidad?.Patente || 'archivo'}`"
            ></iframe>
            <v-img
              v-else
              :key="verPeritajeURL"
              class="peritaje-image"
              :src="verPeritajeURL"
              contain
              max-height="70vh"
            ></v-img>
          </div>
          <div v-else class="text-center py-8">
            <v-icon large color="grey">fas fa-file-times</v-icon>
            <div class="text-body-2 mt-2 grey--text">No se pudo cargar el peritaje</div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="reparaciones-dialog-actions">
          <v-spacer />
          <v-btn
            v-if="verPeritajeURL"
            text
            color="grey darken-1"
            @click="handleEditarPeritaje"
          >
            <v-icon left small>fas fa-pen</v-icon>
            Editar
          </v-btn>
          <v-btn
            v-if="verPeritajeURL"
            text
            color="error"
            :loading="eliminandoPeritaje"
            @click="handleEliminarPeritaje"
          >
            <v-icon left small>fas fa-trash</v-icon>
            Eliminar
          </v-btn>
          <v-btn
            v-if="verPeritajeURL"
            text
            color="info"
            @click="descargarPeritaje"
          >
            <v-icon left small>fas fa-download</v-icon>
            Descargar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Visores de archivo -->
    <PDFViewer
      v-model="dialogPdf"
      :pdf="pdfData"
      :nombre="pdfNombre"
      :titulo="pdfNombre"
    />
    <ImgViewer
      v-model="dialogImg"
      :img="imgData"
      :nombre="imgNombre"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import TableroReparaciones from "@/components/usados/TableroReparaciones.vue";
import SearchDataTable from "../../components/util/SearchDataTable.vue";
import FileDropzone from "../../components/util/FileDropzone.vue";
import PDFViewer from "../../util/plantillas/PDFViewer.vue";
import ImgViewer from "../../components/util/ImgViewer.vue";
import FechaPicker from "../../components/util/FechaPicker.vue";
import { fechaNegocioArgentina, horaNegocioArgentina } from "../../utils/fechas";
import { format_money, format_money_round } from "../../util/utils";


export default {
  name: "Reparaciones",
  components: {
    TableroReparaciones,
    SearchDataTable,
    FileDropzone,
    PDFViewer,
    ImgViewer,
    FechaPicker,
  },
  data() {
    return {
      quickSearch: "",
      finalizadosSearch: "",
      unidadFiltradaId: null,
      mobileColumnTab: 0,
      horariosDisponibles: [],
       cargandoDisponibilidad: false,
       cargandoRecepcionistas: false,
       recepcionistas: [],
       recepcionistasRequestId: 0,
       disponibilidadRequestId: 0,
      fechaMinimaTurno: fechaNegocioArgentina(),
      dialogoNuevaReparacion: false,
      creando: false,
      nuevaReparacion: { ClaveUnidad: null, sucursal: null, conceptos: [] },
      nuevoReparacionConcepto: { categoria: "repuestos", descripcion: "" },
      dialogoAgendar: false,
      reprogramando: false,
      agendando: false,
       menuFecha: false,
         agendarForm: { fecha: "", hora: "", sucursal: null, recepcionistaId: null, tiempoEstimado: 1 },
       seleccionadosTurno: [],
       reparacionAAgendar: null,
      turnoSeleccionado: null,
      dialogoConfirmarFinalizar: false,
      finalizando: false,
      reparacionAFinalizar: null,
      dialogoAgendarPrompt: false,
      reparacionAAgendarPrompt: null,
      itemsEnProceso: [],
      aplicacionesEnProceso: [],
      tableroRequestId: 0,
      dialogoConfirmarCancelar: false,
      cancelando: false,
      reparacionACancelar: null,
      dialogoConfirmarLimpiar: false,
      limpiando: false,
      reparacionALimpiar: null,
      dialogoDetalle: false,
      detalleReparacion: null,
      dialogoCostoTaller: false,
      costoTallerUnidad: null,
      costoTallerLineas: [],
      costoTallerTotal: null,
      costoTallerCargando: false,
      costoTallerError: null,
      costoTallerRequestId: 0,
      costoTallerFiltroDesde: null,
      costoTallerFiltroHasta: null,
      dialogoDetalleOrden: false,
      detalleOrdenUnidad: null,
      ordenesDetalle: [],
      ordenesAnterioresDetalle: [],
      ordenesAnterioresTotalDetalle: 0,
      dialogoHistorial: false,
      historialCargando: false,
      historialError: null,
      historialReparacion: null,
      historialEventos: [],
      historialRequestId: 0,
      historialPuedeAnterior: false,
      historialPuedeSiguiente: false,
      historialPuedeArriba: false,
      historialPuedeAbajo: false,
      pollTimer: null,
      dialogoTexto: false,
      dialogoTextoTitulo: "",
      dialogoTextoValor: "",
      dialogoTextoCallback: null,
      dialogoItem: false,
      dialogoItemTitulo: "",
      dialogoItemCategoria: "repuestos",
      dialogoItemValor: "",
      dialogoItemNotificar: false,
      dialogoItemConceptos: [],
      dialogoItemCallback: null,
      dialogoItemSucursal: null,
      dialogoComentariosItem: false,
      comentariosItemCtx: null,
      comentariosLista: [],
      comentariosCargando: false,
      comentariosError: null,
      comentariosNuevo: "",
      agregandoComentario: false,
      comentariosRequestId: 0,
      dialogoConfirmarEliminar: false,
      dialogoConfirmarEliminarMensaje: "",
      dialogoConfirmarEliminarCallback: null,
      eliminandoItem: false,
      // Peritaje
      dialogoPeritaje: false,
      peritajeUnidad: null,
      peritajeArchivos: [],
      peritajeTiposPermitidos: [".pdf", ".jpg", ".jpeg", ".png", ".webp"],
      peritajeError: null,
      subiendoPeritaje: false,
      dialogoVerPeritaje: false,
      verPeritajeUnidad: null,
      verPeritajeURL: null,
      verPeritajeCargando: false,
      eliminandoPeritaje: false,
      // Visores de archivo
      dialogPdf: false,
      pdfData: null,
      pdfNombre: "",
      dialogImg: false,
      imgData: null,
      imgNombre: "",
      finalizadosHeaders: [
        { text: "Patente", value: "Patente" },
        { text: "Empresa de origen", value: "Empresa" },
        { text: "Modelo", value: "Modelo" },
        { text: "Color", value: "Color" },
        { text: "Finalizado", value: "FechaFinalizado" },
        { text: "Responsable", value: "Responsable" },
        { text: "Costo Taller", value: "CostoTaller", align: "end" },
        { text: "Acciones", value: "acciones", sortable: false, align: "end" },
      ],
      costoTallerHeaders: [
        { text: "Orden", value: "NumeroOrden", width: "90px" },
        { text: "Cierre", value: "Cierre", width: "100px" },
        { text: "Tipo", value: "TipodeCargo", width: "90px" },
        { text: "Repuesto", value: "Repuesto" },
        { text: "Descripción", value: "Descripcion" },
        { text: "Cant.", value: "Cant", align: "end", width: "70px" },
        { text: "Precio", value: "Precio", align: "end" },
        { text: "Monto", value: "Monto", align: "end" },
      ],
      // ABM de destinatarios de alertas
      dialogoDestinatarios: false,
      destinatarioSucursalNumero: null,
      destinatarioTipoAlerta: "repuestos_completos",
      destinatarioNuevoEmail: "",
      destinatarioNuevoTelefono: "",
      destinatarioNuevoNombre: "",
      destinatarios: [],
      cargandoDestinatarios: false,
      guardandoDestinatario: false,
      destinatarioBajandoId: null,
      destinatarioAltaId: null,
      dialogoEditarDestinatario: false,
      guardandoEdicionDestinatario: false,
      destinatarioEditar: {
        id: null,
        sucursal_numero: null,
        tipo_alerta: "repuestos_completos",
        email: "",
        telefono: "",
        nombre: "",
      },
      headersDestinatarios: [
        { text: "ID", value: "id", width: "90px" },
        { text: "Sucursal", value: "sucursal_numero" },
        { text: "Tipo de alerta", value: "tipo_alerta", width: "220px" },
        { text: "Nombre", value: "nombre" },
        { text: "Email", value: "email" },
        { text: "Teléfono", value: "telefono" },
        { text: "Estado actual", value: "activo", width: "140px" },
        { text: "Acciones", value: "acciones", align: "end", sortable: false, width: "220px" },
      ],
      // Mobile: cierre de diálogos con el botón "atrás" (entrada fantasma en el historial).
      _popStateDialogHandler: null,
      _dialogHistoryPushed: false,
    };
  },
  computed: {
    ...mapState({
      recibidos: (state) => state.usados.reparaciones.recibidos || [],
      espera_repuestos: (state) => state.usados.reparaciones.espera_repuestos || [],
      agendado: (state) => state.usados.reparaciones.agendado || [],
      terceros: (state) => state.usados.reparaciones.terceros || [],
      en_proceso: (state) => state.usados.reparaciones.en_proceso || [],
      finalizados: (state) => state.usados.reparaciones.finalizados || [],
      loading: (state) => state.usados.reparaciones.loading,
      permisosStore: (state) => state.usados.reparaciones.permisos,
      sucursalesUsuario: (state) => state.usados.reparaciones.sucursales || [],
      sucursales: (state) => state.genericos.sucursales || [],
    }),
    fechaFormateada() {
      if (!this.agendarForm.fecha) return '';
      const [year, month, day] = this.agendarForm.fecha.split('-');
      return `${day}/${month}/${year}`;
    },
    permisos() {
      const p = this.permisosStore;
      return {
        lectura: !!(p && p.lectura),
        gestion: !!(p && p.gestion),
        repuestos: !!(p && p.repuestos),
        gestorRepuestos: !!(p && p.gestorRepuestos),
        destinatarios: !!(p && p.destinatarios),
      };
    },
    esGestorRepuestos() {
      return this.permisos.gestorRepuestos;
    },
    puedeGestionar() {
      return this.permisos.gestion && !this.esGestorRepuestos;
    },
    unidadesSinReparacion() {
      return this.recibidosUnicos.filter((item) => !item.ReparacionId);
    },
    recibidosUnicos() {
      const cards = new Map();
      for (const item of this.recibidos) {
        const clave = this.claveUnidad(item);
        if (!cards.has(clave) || (item.ReparacionId && !cards.get(clave).ReparacionId)) {
          cards.set(clave, item);
        }
      }
      return [...cards.values()];
    },
    // Filtro del detalle de costo de taller por fecha de cierre: el detalle llega
    // completo al dialog, el rango se aplica en memoria sobre la columna Cierre y
    // el total se recalcula con las líneas visibles.
    costoTallerFiltroActivo() {
      return !!this.costoTallerFiltroDesde || !!this.costoTallerFiltroHasta;
    },
    costoTallerLineasVisibles() {
      if (!this.costoTallerFiltroActivo) return this.costoTallerLineas;
      const desde = this.fechaCostoTallerANumero(this.costoTallerFiltroDesde);
      const hasta = this.fechaCostoTallerANumero(this.costoTallerFiltroHasta);
      return this.costoTallerLineas.filter((linea) => {
        const cierre = this.fechaCostoTallerANumero(linea.Cierre);
        if (cierre === null) return false;
        if (desde !== null && cierre < desde) return false;
        if (hasta !== null && cierre > hasta) return false;
        return true;
      });
    },
    costoTallerTotalVisible() {
      if (!this.costoTallerFiltroActivo) return this.costoTallerTotal;
      return this.costoTallerLineasVisibles.reduce(
        (total, linea) => total + (Number(linea.Monto) || 0),
        0
      );
    },
    // Los cargos sin cierre no pueden ubicarse en el rango; se cuentan aparte.
    costoTallerSinCierre() {
      if (!this.costoTallerFiltroActivo) return 0;
      return this.costoTallerLineas.filter(
        (linea) => this.fechaCostoTallerANumero(linea.Cierre) === null
      ).length;
    },
    historialTiempo() {
      const primero = this.historialEventos[0];
      return primero ? this.tiempoTranscurrido(primero.fecha, true) : "";
    },
    // Unifica en una sola card las notificaciones exitosas de una misma acción
    // enviadas por varios canales (WhatsApp, correo electrónico, etc.) dentro de
    // una ventana breve; un envío con error u omitido conserva su card propia.
    historialEventosAgrupados() {
      const ventanaMs = 60000;
      const agrupados = [];
      for (const evento of this.historialEventos || []) {
        const anterior = agrupados[agrupados.length - 1];
        const puedeUnir = this.alertaEnviada(evento)
          && anterior
          && this.alertaEnviada(anterior)
          && anterior.evento === evento.evento
          && new Date(evento.fecha).getTime() - new Date(anterior.fecha).getTime() <= ventanaMs;
        if (puedeUnir) {
          if (evento.canal && !anterior.canales.includes(evento.canal)) {
            anterior.canales.push(evento.canal);
          }
          continue;
        }
        agrupados.push(this.alertaEnviada(evento)
          ? { ...evento, canales: evento.canal ? [evento.canal] : [] }
          : evento);
      }
      return agrupados;
    },
    columnasFiltradas() {
      const search = this.quickSearch
        ? this.quickSearch.toLowerCase().trim()
        : "";

      const filtrar = (items) => {
        const porUnidad = this.unidadFiltradaId
          ? items.filter((item) => this.claveUnidad(item) === this.unidadFiltradaId)
          : items;
        if (!search) return porUnidad;
        return porUnidad.filter((item) => {
          return (
            (item.Patente && item.Patente.toLowerCase().includes(search)) ||
            (item.Modelo && item.Modelo.toLowerCase().includes(search)) ||
            (item.Nombre && item.Nombre.toLowerCase().includes(search))
          );
        });
      };

      const columnas = [
        { titulo: "Recibidos", estado: "recibidos", items: filtrar(this.recibidosUnicos) },
        { titulo: "En espera de repuestos", estado: "espera_repuestos", items: filtrar(this.espera_repuestos) },
        { titulo: "Mano de obra", estado: "agendado", items: filtrar(this.agendado) },
        { titulo: "Trabajos de terceros", estado: "terceros", items: filtrar(this.terceros) },
        { titulo: "En proceso", estado: "en_proceso", items: filtrar(this.en_proceso) },
      ];
      return this.esGestorRepuestos
        ? columnas.filter((columna) => columna.estado === "espera_repuestos")
        : columnas;
    },
    columnasVisiblesKanban() {
      return this.columnasFiltradas;
    },
    finalizadosFiltrados() {
      return this.finalizados;
    },
    // Sucursales con agendamiento habilitado (ver SUCURSALES_AGENDABLES en el controller).
    sucursalesAgendables() {
      const agendables = [1, 2];
      return (this.sucursales || []).filter((s) => agendables.includes(Number(s.id)));
    },
    sucursalesTurno() {
      return [
        { id: 1, nombre: "Salta" },
        { id: 2, nombre: "Dycar Jujuy" },
      ];
    },
    // Alcance de sucursal: un usuario con sucursales asignadas solo ve y opera
    // esas cards; sin asignaciones mantiene el alcance global (ver getTablero).
    estaScopeadoPorSucursal() {
      return this.sucursalesUsuario.length > 0;
    },
    sucursalesTurnoDisponibles() {
      if (!this.estaScopeadoPorSucursal) return this.sucursalesTurno;
      return this.sucursalesTurno.filter((sucursal) =>
        this.sucursalesUsuario.includes(Number(sucursal.id))
      );
    },
    // Sucursal por defecto para usuarios scopeados: la primera opción realmente
    // agendable (1/2). Si el alcance no incluye ninguna, devuelve null para no
    // fijar un valor inválido en los selectores.
    sucursalAlcancePorDefecto() {
      if (!this.estaScopeadoPorSucursal) return null;
      const opcion = this.sucursalesTurnoDisponibles[0];
      return opcion ? opcion.id : null;
    },
    tiposDestinatarioAlertaOptions() {
      return [
        { value: "nuevos_repuestos", text: "Nuevos repuestos" },
        { value: "repuestos_completos", text: "Repuestos completos" },
        { value: "reparacion_finalizada", text: "Reparación finalizada" },
        { value: "trabajo_chapa_pintura", text: "Alerta de trabajo de chapa y pintura" },
        { value: "repuestos_pendientes", text: "Alerta de repuestos pendientes" },
      ];
    },
    destinatariosFiltrados() {
      const sucursalNumero = Number(this.destinatarioSucursalNumero);
      return this.destinatarios.filter((d) => {
        const coincideSucursal =
          !Number.isInteger(sucursalNumero) ||
          sucursalNumero <= 0 ||
          Number(d.sucursal_numero) === sucursalNumero;
        const coincideTipo =
          !this.destinatarioTipoAlerta ||
          d.tipo_alerta === this.destinatarioTipoAlerta;
        return coincideSucursal && coincideTipo;
      });
    },
    turnosGestionados() {
      return (this.reparacionAAgendar && this.reparacionAAgendar.Turnos) || [];
    },
    chipsTurno() {
      const reparacion = this.reparacionAAgendar;
      const trabajos = (reparacion && reparacion.Trabajos) || [];
      const items = [];
      for (const trabajo of trabajos) {
        for (const it of trabajo.Items || []) {
          items.push(it);
        }
      }
      const chips = [];
      const repuestos = items.filter((it) => it.Categoria === "repuestos");
      const repuestosPendientes = repuestos.filter((it) => !it.Completado);
      // Un único chip de repuestos a nivel unidad cuando no queda nada pendiente
      // en ningún trabajo técnico.
      if (repuestos.length && repuestosPendientes.length === 0) {
        chips.push({
           key: `repuestos:${reparacion.ReparacionId}`,
           tipo: "repuestos",
           label: `Aplicación de ${repuestos.map((it) => it.Descripcion).join(", ")}`,
           trabajoIds: trabajos
             .filter((trabajo) => (trabajo.Items || []).some((it) => it.Categoria === "repuestos" && it.Completado))
             .map((trabajo) => Number(trabajo.Id))
             .filter((id) => Number.isInteger(id) && id > 0),
           manoObraItemIds: [],
         });
      }
      // Un chip por concepto manual de mano de obra; la mano de obra automática
      // (EsAutomatico) queda excluida del listado.
      for (const item of items.filter((it) => it.Categoria === "mano_obra" && !it.EsAutomatico)) {
        chips.push({
           key: `mano_obra:${item.Id}`,
           tipo: "mano_obra",
           label: item.Descripcion || "Mano de obra",
           trabajoIds: [],
           manoObraItemIds: [Number(item.Id)].filter((id) => Number.isInteger(id) && id > 0),
         });
      }
      return chips;
    },
    seleccionTurnoValida() {
      const keysDisponibles = this.chipsTurno.map((chip) => chip.key);
      const cantidad = this.seleccionadosTurno.filter((key) => keysDisponibles.includes(key)).length;
      return cantidad >= 1 && cantidad <= 2;
    },
    disponibilidadGestionCompleta() {
      return !!(
        this.agendarForm.sucursal
        && this.agendarForm.recepcionistaId
        && this.agendarForm.fecha
        && this.tiempoManoObraValido(this.agendarForm.tiempoEstimado)
      );
    },
    horaMinimaHoy() {
      return horaNegocioArgentina();
    },
    horariosDisponiblesTurno() {
      return this.agendarForm.fecha === fechaNegocioArgentina()
        ? this.horariosDisponibles.filter((hora) => hora >= this.horaMinimaHoy)
        : this.horariosDisponibles;
    },
    puedeConfirmarTurno() {
      return this.disponibilidadGestionCompleta
        && (this.reprogramando || this.seleccionTurnoValida)
        && !!this.agendarForm.hora
        && this.horariosDisponibles.includes(this.agendarForm.hora)
        && this.reglaFechaTurno(this.agendarForm.fecha) === true
        && this.reglaHoraTurno(this.agendarForm.hora) === true;
    },
    esPeritajePDF() {
      if (!this.verPeritajeURL) return false;
      const url = String(this.verPeritajeURL).toLowerCase();
      return url.endsWith(".pdf") || url.includes(".pdf?");
    },
    // Mobile/tablet: habilita el cierre de diálogos con el botón "atrás".
    esMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    // Hay algún diálogo abierto (sirve para sincronizar el historial del navegador).
    anyDialogOpen() {
      return (
        this.dialogoHistorial ||
        this.dialogoNuevaReparacion ||
        this.dialogoAgendar ||
        this.dialogoAgendarPrompt ||
        this.dialogoConfirmarFinalizar ||
        this.dialogoConfirmarCancelar ||
        this.dialogoConfirmarLimpiar ||
        this.dialogoConfirmarEliminar ||
        this.dialogoTexto ||
        this.dialogoItem ||
        this.dialogoDetalle ||
        this.dialogoCostoTaller ||
        this.dialogoDetalleOrden ||
        this.dialogoDestinatarios ||
        this.dialogoEditarDestinatario ||
        this.dialogoPeritaje ||
        this.dialogoVerPeritaje
      );
    },
  },
  watch: {
    "agendarForm.sucursal"(sucursal, sucursalAnterior) {
      if (Number(sucursal) === Number(sucursalAnterior)) return;
      this.agendarForm.recepcionistaId = null;
      this.agendarForm.hora = "";
      this.horariosDisponibles = [];
      if (sucursal) {
        this.cargarRecepcionistas(sucursal);
      } else {
        this.recepcionistasRequestId += 1;
        this.cargandoRecepcionistas = false;
        this.recepcionistas = [];
      }
    },
    "agendarForm.fecha"() {
      this.agendarForm.hora = "";
      this.refrescarDisponibilidadGestion();
    },
    "agendarForm.recepcionistaId"() {
      this.agendarForm.hora = "";
      this.refrescarDisponibilidadGestion();
    },
    "agendarForm.tiempoEstimado"() {
      this.agendarForm.hora = "";
      this.refrescarDisponibilidadGestion();
    },
    // Sucursal por defecto del alta: con alcance definido se fija la primera
    // sucursal del usuario; sin alcance se deriva de la ubicación de la unidad
    // (puede quedar null y el backend la deriva/valida igual).
    "nuevaReparacion.ClaveUnidad"(clave) {
      if (!clave) {
        this.nuevaReparacion.sucursal = null;
        return;
      }
      if (this.estaScopeadoPorSucursal) {
        this.nuevaReparacion.sucursal = this.sucursalAlcancePorDefecto;
        return;
      }
      const unidad = this.unidadesSinReparacion.find((item) => this.claveUnidad(item) === clave);
      this.nuevaReparacion.sucursal = this.sucursalPorUbicacionDeUnidad(unidad);
    },
    /**
     * Sincroniza el historial del navegador para que el botón "atrás"
     * del celular cierre el diálogo abierto en vez de salir de la página.
     */
    anyDialogOpen(val) {
      this._syncDialogHistory(val);
    },
    "$vuetify.breakpoint.smAndDown"() {
      this._syncDialogHistory(this.anyDialogOpen);
    },
  },
  methods: {
    formatFecha(fecha) {
      if (!fecha) return "";
      const d = new Date(fecha);
      if (isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    formatCostoTaller(valor) {
      return format_money_round(valor);
    },
    formatMoney(valor) {
      return format_money(valor);
    },
    // detallado=true descompone el tiempo en meses, días y horas (chip del
    // encabezado del historial); false devuelve solo la unidad mayor (eventos).
    tiempoTranscurrido(fecha, detallado = false) {
      if (!fecha) return "";
      const d = new Date(fecha);
      if (isNaN(d.getTime())) return "";
      const minutos = Math.floor((Date.now() - d.getTime()) / 60000);
      if (minutos < 0) return "";
      if (minutos < 1) return "menos de 1 minuto";
      if (minutos < 60 && !detallado) return `${minutos} minuto${minutos === 1 ? "" : "s"}`;
      const horas = Math.floor(minutos / 60);
      if (horas < 24 && !detallado) return `${horas} hora${horas === 1 ? "" : "s"}`;
      const dias = Math.floor(horas / 24);
      if (dias < 30 && !detallado) return `${dias} día${dias === 1 ? "" : "s"}`;
      const meses = Math.floor(dias / 30);
      if (!detallado) return `${meses} mes${meses === 1 ? "" : "es"}`;
      const partes = [];
      if (meses > 0) partes.push(`${meses} mes${meses === 1 ? "" : "es"}`);
      const diasRestantes = dias - meses * 30;
      if (diasRestantes > 0) partes.push(`${diasRestantes} día${diasRestantes === 1 ? "" : "s"}`);
      const horasRestantes = horas - dias * 24;
      if (horasRestantes > 0) partes.push(`${horasRestantes} hora${horasRestantes === 1 ? "" : "s"}`);
      if (!partes.length) return `${minutos} minuto${minutos === 1 ? "" : "s"}`;
      if (partes.length === 1) return partes[0];
      return `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1]}`;
    },
    formatTurno(turno) {
      const fecha = turno && turno.FechaTurno ? new Date(turno.FechaTurno) : null;
      const fechaTexto = fecha && !isNaN(fecha.getTime())
        ? `${String(fecha.getDate()).padStart(2, "0")}/${String(fecha.getMonth() + 1).padStart(2, "0")}/${fecha.getFullYear()}`
        : "—";
      return `${fechaTexto} ${turno && turno.Hora ? String(turno.Hora).slice(0, 5) : ""}`.trim();
    },
    tituloEvento(evento) {
      const categoria = this.categoriaEvento(evento);
      if (categoria) return this.categoriaLabel(categoria);
      const tipo = typeof evento === "string" ? evento : String((evento && evento.evento) || "");
      const nombreTarea = this.nombreTareaEvento(evento);
      if (nombreTarea) return nombreTarea;
      const labels = {
        recibido_detectado: "Vehículo recibido",
        reparacion_creada: "Reparación creada",
        trabajo_creado: "Trabajo creado",
        trabajo_editado: "Trabajo editado",
        trabajo_eliminado: "Trabajo eliminado",
        item_creado: "Tarea agregada",
        item_editado: "Tarea actualizada",
        item_eliminado: "Tarea eliminada",
        item_completado: "Tarea actualizada",
        trabajo_completado: "Trabajo completado",
        estado_cambiado: "Estado actualizado",
        turno_reservado: "Turno reservado",
        turno_reprogramado: "Turno reprogramado",
        turno_cancelado: "Turno cancelado",
        orden_servicio_detectada: "Orden de servicio registrada",
        en_proceso: "Trabajo iniciado",
        reparacion_finalizada: "Reparación finalizada",
        nuevos_repuestos: "Notificaciones por nuevos repuestos",
        repuestos_completos: "Repuestos disponibles",
        trabajo_chapa_pintura: "Alerta de trabajo de chapa y pintura",
        repuestos_pendientes: "Alerta de repuestos pendientes",
        email_reenviado: "Aviso reenviado",
        reparacion_limpiada: "Reparación limpiada",
        peritaje_cargado: "Peritaje cargado",
        peritaje_eliminado: "Peritaje eliminado",
      };
      if (labels[tipo]) return labels[tipo];
      return String(tipo || "Evento").replace(/_/g, " ").replace(/^./, (letra) => letra.toUpperCase());
    },
    // Los eventos de concepto y de trabajo usan el nombre registrado como
    // título cuando el log lo trae (o el nombre actual provisto por la API);
    // los registros históricos sin nombre conservan la etiqueta genérica.
    nombreTareaEvento(evento) {
      if (!evento || typeof evento !== "object") return "";
      const tipos = [
        "item_creado",
        "item_editado",
        "item_completado",
        "item_eliminado",
        "trabajo_creado",
        "trabajo_editado",
        "trabajo_completado",
        "trabajo_eliminado",
      ];
      if (!tipos.includes(String(evento.evento || ""))) return "";
      const descripcion = evento.detalle && evento.detalle.descripcion;
      return descripcion ? String(descripcion) : "";
    },
    // Los eventos de concepto (repuestos, mano de obra y terceros) se titulan
    // con la categoría y muestran el nombre del concepto como subtítulo; la
    // categoría deja de repetirse como fila "Categoría" dentro del detalle.
    categoriaEvento(evento) {
      if (!evento || typeof evento !== "object") return "";
      const categoria = evento.detalle && evento.detalle.categoria;
      return ["repuestos", "mano_obra", "terceros"].includes(categoria) ? categoria : "";
    },
    subtituloEvento(evento) {
      return this.categoriaEvento(evento) ? this.nombreTareaEvento(evento) : "";
    },
    eventoNuevo(evento) {
      return !!evento && (evento.evento === "item_creado" || evento.evento === "trabajo_creado");
    },
    eventoCompletado(evento) {
      if (!evento) return false;
      if (evento.evento === "trabajo_completado") return true;
      return evento.evento === "item_completado"
        && !!(evento.detalle && evento.detalle.completado);
    },
    eventoEliminado(evento) {
      return !!evento && (evento.evento === "item_eliminado" || evento.evento === "trabajo_eliminado");
    },
    iconoEvento(evento) {
      if (evento.origen === "alerta") return "fas fa-bell";
      if (String(evento.evento).includes("turno")) return "fas fa-calendar-alt";
      if (String(evento.evento).includes("item") || String(evento.evento).includes("trabajo")) return "fas fa-tools";
      if (String(evento.evento).includes("finalizada")) return "fas fa-check-circle";
      return "fas fa-circle";
    },
    canalEvento(canal) {
      const labels = { smtp: "Correo electrónico", resend: "Correo electrónico", atom: "WhatsApp", interna: "Aviso interno", push: "Aviso en la aplicación" };
      return labels[canal] || canal || "Alerta";
    },
    // Las notificaciones enviadas con éxito se resumen en una sola línea legible
    // según el canal; los envíos con error u omitidos conservan sus chips y detalle.
    alertaEnviada(evento) {
      return !!evento && evento.origen === "alerta" && evento.resultado === "aceptado";
    },
    mensajeEnvioNotificacion(evento) {
      const labels = {
        smtp: "correo electrónico",
        resend: "correo electrónico",
        atom: "WhatsApp",
        interna: "aviso interno",
        push: "aviso en la aplicación",
      };
      const canales = (evento && Array.isArray(evento.canales) && evento.canales.length)
        ? evento.canales
        : [evento && evento.canal];
      const nombres = [];
      for (const canal of canales) {
        const nombre = labels[canal] || canal;
        if (nombre && !nombres.includes(nombre)) nombres.push(nombre);
      }
      return `Se envió notificación mediante ${this.listarNombres(nombres)}`;
    },
    listarNombres(nombres) {
      if (!nombres.length) return "alerta";
      if (nombres.length === 1) return nombres[0];
      return `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}`;
    },
    resultadoEvento(resultado) {
      const labels = { aceptado: "Aceptado", error: "Error", omitido: "Omitido" };
      return labels[resultado] || resultado || "Sin resultado";
    },
    colorResultadoEvento(resultado) {
      return { aceptado: "success", error: "error", omitido: "grey" }[resultado] || "info";
    },
    // Familia cromática del evento: replica los colores de categoría del tablero
    // (repuestos amber, mano de obra blue, terceros purple), suma peritaje teal y
    // alerta info; el resto queda neutro.
    familiaEvento(evento) {
      if (!evento) return "neutro";
      if (evento.tipoAlerta === "nuevos_repuestos") return "repuestos";
      const categoria = evento.detalle?.categoria;
      if (categoria === "repuestos" || categoria === "mano_obra" || categoria === "terceros") {
        return categoria;
      }
      const tipo = String(evento.evento || "");
      if (["turno_reservado", "turno_reprogramado", "turno_cancelado"].includes(tipo)) return "turno";
      if (tipo === "orden_servicio_detectada") return "orden";
      if (evento.origen === "alerta") return "alerta";
      if (tipo.startsWith("peritaje")) return "peritaje";
      return "neutro";
    },
    // Arriba queda el trabajo interno (mano de obra, turnos, órdenes y seguimiento);
    // abajo, lo externo (repuestos y trabajos de terceros).
    posicionEvento(evento) {
      if (evento && (evento.tipoAlerta === "nuevos_repuestos" || evento.tipoAlerta === "repuestos_completos")) return "abajo";
      const categoria = evento && evento.detalle && evento.detalle.categoria;
      if (categoria === "repuestos" || categoria === "terceros") return "abajo";
      return "arriba";
    },
    colorEvento(evento) {
      const colores = {
        repuestos: "amber darken-3",
        mano_obra: "blue darken-3",
        terceros: "purple darken-3",
        turno: "cyan darken-3",
        orden: "green darken-2",
        peritaje: "teal darken-1",
        alerta: "info",
        neutro: "blue-grey darken-1",
      };
      return colores[this.familiaEvento(evento)] || "blue-grey darken-1";
    },
    etiquetaDetalle(clave) {
      const labels = {
        descripcion: "Descripción",
        categoria: "Categoría",
        completado: "Completado",
        estado: "Estado",
        motivo: "Motivo",
        estadoAnterior: "Estado anterior",
        estadoNuevo: "Estado nuevo",
        fecha: "Fecha",
        hora: "Hora",
        sucursal: "Sucursal",
        mensaje: "Detalle",
      };
      return labels[clave] || "";
    },
    valorDetalle(valor, clave) {
      if (clave === "categoria") return this.categoriaLabel(valor);
      if (["estado", "estadoAnterior", "estadoNuevo"].includes(clave)) {
        return String(valor).replace(/_/g, " ").replace(/^./, (letra) => letra.toUpperCase());
      }
      if (typeof valor === "boolean") return valor ? "Sí" : "No";
      if (Array.isArray(valor)) return valor.filter((item) => typeof item !== "object").join(", ");
      if (valor && typeof valor === "object") return "";
      return String(valor);
    },
    detalleEventoItems(evento) {
      const detalle = evento && evento.detalle;
      if (!detalle || typeof detalle !== "object") return [];
      const clavesPermitidas = [
        "descripcion",
        "categoria",
        "completado",
        "estado",
        "estadoAnterior",
        "estadoNuevo",
        "motivo",
        "fecha",
        "hora",
        "sucursal",
        "mensaje",
      ];
      // El título o subtítulo ya muestra la categoría y el nombre de la tarea,
      // y el chip ya indica el completado: se omiten del detalle para no
      // duplicar la información.
      const clavesOcultas = [];
      if (this.nombreTareaEvento(evento)) clavesOcultas.push("descripcion");
      if (this.categoriaEvento(evento)) clavesOcultas.push("categoria");
      if (this.eventoCompletado(evento)) clavesOcultas.push("completado");
      return clavesPermitidas
        .filter((clave) => !clavesOcultas.includes(clave))
        .filter((clave) => detalle[clave] !== null && detalle[clave] !== undefined && detalle[clave] !== "")
        .map((clave) => ({ clave, etiqueta: this.etiquetaDetalle(clave), valor: this.valorDetalle(detalle[clave], clave) }))
        .filter((detalleItem) => detalleItem.valor !== "");
    },
    async abrirHistorial(item) {
      if (!item || !item.ReparacionId) return;
      const requestId = ++this.historialRequestId;
      this.historialReparacion = item;
      this.historialEventos = [];
      this.historialError = null;
      this.historialCargando = true;
      this.dialogoHistorial = true;
      try {
        const result = await this.$store.dispatch("usados/obtenerHistorialReparacion", { id: item.ReparacionId });
        if (requestId !== this.historialRequestId) return;
        if (!result || result.exito !== 1) {
          this.historialError = result?.message || result?.msj || "No se pudo cargar el historial.";
          return;
        }
        this.historialReparacion = result.data?.reparacion || item;
        this.historialEventos = result.data?.eventos || [];
        this.$nextTick(this.actualizarControlesHistorial);
      } catch (error) {
        if (requestId === this.historialRequestId) {
          this.historialError = error.message || "No se pudo cargar el historial.";
        }
      } finally {
        if (requestId === this.historialRequestId) this.historialCargando = false;
      }
    },
    cerrarHistorial() {
      this.dialogoHistorial = false;
    },
    onHistorialDialogChange(abierto) {
      if (abierto) return;
      this.historialRequestId += 1;
      this.historialCargando = false;
      this.historialError = null;
      this.historialReparacion = null;
      this.historialEventos = [];
      this.historialPuedeAnterior = false;
      this.historialPuedeSiguiente = false;
      this.historialPuedeArriba = false;
      this.historialPuedeAbajo = false;
    },
    actualizarControlesHistorial() {
      const timeline = this.$refs.historialTimeline;
      if (!timeline) {
        this.historialPuedeAnterior = false;
        this.historialPuedeSiguiente = false;
        this.historialPuedeArriba = false;
        this.historialPuedeAbajo = false;
        return;
      }
      this.historialPuedeAnterior = timeline.scrollLeft > 2;
      this.historialPuedeSiguiente = timeline.scrollLeft + timeline.clientWidth < timeline.scrollWidth - 2;
      this.historialPuedeArriba = timeline.scrollTop > 2;
      this.historialPuedeAbajo = timeline.scrollTop + timeline.clientHeight < timeline.scrollHeight - 2;
    },
    desplazarHistorial(direccion) {
      const timeline = this.$refs.historialTimeline;
      if (!timeline) return;
      const distancia = Math.max(280, Math.floor(timeline.clientWidth * 0.8));
      if (typeof timeline.scrollBy === "function") {
        timeline.scrollBy({ left: direccion * distancia, behavior: "smooth" });
      } else {
        timeline.scrollLeft += direccion * distancia;
      }
    },
    desplazarHistorialVertical(direccion) {
      const timeline = this.$refs.historialTimeline;
      if (!timeline) return;
      const distancia = Math.max(200, Math.floor(timeline.clientHeight * 0.8));
      if (typeof timeline.scrollBy === "function") {
        timeline.scrollBy({ top: direccion * distancia, behavior: "smooth" });
      } else {
        timeline.scrollTop += direccion * distancia;
      }
    },
    tiempoManoObraValido(valor) {
      const tiempo = Number(valor);
      return Number.isFinite(tiempo) && tiempo >= 0.25 && Number.isInteger(tiempo * 4);
    },
    reglaTiempoManoObra(valor) {
      return this.tiempoManoObraValido(valor)
        || "Ingrese al menos 0,25 horas, en incrementos de 0,25";
    },
    reglaFechaTurno(valor) {
      if (!valor) return true;
      return String(valor).slice(0, 10) >= fechaNegocioArgentina()
        || "La fecha no puede ser anterior a hoy";
    },
    reglaHoraTurno(valor) {
      if (!valor || this.agendarForm.fecha !== fechaNegocioArgentina()) return true;
      return String(valor).slice(0, 5) >= this.horaMinimaHoy
        || "No se puede seleccionar una hora anterior a la actual";
    },
    categoriaLabel(categoria) {
      const map = { repuestos: "Repuesto", mano_obra: "Mano de obra", terceros: "Terceros" };
      return map[categoria] || categoria;
    },
    categoriaColor(categoria) {
      const map = {
        repuestos: "amber",
        mano_obra: "blue",
        terceros: "purple",
      };
      return map[categoria] || "grey";
    },
    nombreEmpresa(empresa) {
      return { 1: "Dycar", 2: "Eurodycar", 3: "GDB" }[Number(empresa)] || "Origen desconocido";
    },
    claveUnidad(item) {
      return item ? `${Number(item.Empresa ?? 1)}:${Number(item.UsadoID)}` : null;
    },
    filtrarUnidad(item, queryText) {
      const q = String(queryText || "").toLowerCase();
      return (
        String(item.Patente || "").toLowerCase().includes(q) ||
        String(item.Marca || "").toLowerCase().includes(q) ||
        String(item.Modelo || "").toLowerCase().includes(q) ||
        this.nombreEmpresa(item.Empresa).toLowerCase().includes(q)
      );
    },
    async fetchTablero() {
      const requestId = ++this.tableroRequestId;
      this.$store.commit("usados/SET_REPARACIONES_LOADING", true);
      try {
        const result = await this.$store.dispatch("usados/fetchReparaciones");
        // Si otra recarga más nueva ya se disparó, esta respuesta es obsoleta:
        // se ignora para no pisar el tablero con datos viejos.
        if (requestId !== this.tableroRequestId) return result;
        if (result && result.exito === 1) {
          this.$store.commit("usados/SET_REPARACIONES_DATA", {
            recibidos: result.data.recibidos || [],
            espera_repuestos: result.data.espera_repuestos || [],
            agendado: result.data.agendado || [],
            terceros: result.data.terceros || [],
            en_proceso: result.data.en_proceso || [],
            finalizados: result.data.finalizados || [],
          });
          this.$store.commit("usados/SET_REPARACIONES_PERMISOS", result.permisos || {});
          this.$store.commit("usados/SET_REPARACIONES_SUCURSALES", result.sucursales || []);
        } else {
          this.$store.dispatch("show_snackbar", {
            text: result.message || "Error al cargar el tablero",
            color: "error",
          });
        }
        return result;
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al cargar el tablero: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        if (requestId === this.tableroRequestId) {
          this.$store.commit("usados/SET_REPARACIONES_LOADING", false);
        }
      }
    },
    buscarReparacionEnTablero(data, reparacionId) {
      const colecciones = ["recibidos", "espera_repuestos", "agendado", "terceros", "en_proceso"];
      for (const coleccion of colecciones) {
        const reparacion = (data?.[coleccion] || []).find(
          (item) => Number(item.ReparacionId) === Number(reparacionId)
        );
        if (reparacion) return reparacion;
      }
      return null;
    },

    // ---------------------------------------------------------------------
    // Alta + jerarquía
    // ---------------------------------------------------------------------
    abrirNuevaReparacion() {
      this.nuevaReparacion = { ClaveUnidad: null, sucursal: null, conceptos: [] };
      this.nuevoReparacionConcepto = { categoria: "repuestos", descripcion: "" };
      this.dialogoNuevaReparacion = true;
    },
    agregarConceptoForm() {
      const descripcion = String(this.nuevoReparacionConcepto.descripcion || "").trim();
      if (!descripcion) return;
      this.nuevaReparacion.conceptos.push({
        categoria: this.nuevoReparacionConcepto.categoria,
        descripcion,
      });
      this.nuevoReparacionConcepto.descripcion = "";
      this.$nextTick(() => {
        if (this.$refs.inputConceptoNuevaReparacion) this.$refs.inputConceptoNuevaReparacion.focus();
      });
    },
    quitarConcepto(idx) {
      this.nuevaReparacion.conceptos.splice(idx, 1);
    },
    async confirmarNuevaReparacion() {
      const unidad = this.unidadesSinReparacion.find(
        (item) => this.claveUnidad(item) === this.nuevaReparacion.ClaveUnidad
      );
      if (!unidad) {
        this.$store.dispatch("show_snackbar", { text: "Seleccioná una unidad", color: "warning" });
        return;
      }
      const conceptos = this.nuevaReparacion.conceptos
        .filter((c) => String(c.descripcion || "").trim())
        .map((c) => ({ categoria: c.categoria, descripcion: c.descripcion.trim() }));
      if (!conceptos.length) {
        this.$store.dispatch("show_snackbar", { text: "Agregá al menos una tarea", color: "warning" });
        return;
      }
      if (!this.nuevaReparacion.sucursal) {
        this.$store.dispatch("show_snackbar", { text: "Seleccioná la sucursal", color: "warning" });
        return;
      }

      this.creando = true;
      try {
        const result = await this.$store.dispatch("usados/crearReparacion", {
          UsadoID: unidad.UsadoID,
          Empresa: unidad.Empresa ?? 1,
          sucursal: this.nuevaReparacion.sucursal,
          conceptos,
        });
        if (!result || result.exito !== 1) {
          this.$store.dispatch("show_snackbar", {
            text: result?.message || "Error al crear la reparación",
            color: "error",
          });
          return;
        }
        this.dialogoNuevaReparacion = false;
        this.$store.dispatch("show_snackbar", { text: "Reparación creada", color: "success" });
        await this.fetchTablero();
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al crear: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        this.creando = false;
      }
    },
    // Tres secciones fijas para el detalle de una card finalizada, agregando
    // los conceptos de todos los trabajos técnicos sin nombre de trabajo.
    gruposDetalle() {
      const orden = ["repuestos", "mano_obra", "terceros"];
      const labels = {
        repuestos: "Repuestos",
        mano_obra: "Mano de obra",
        terceros: "Trabajos de terceros",
      };
      const grupos = {};
      for (const trabajo of (this.detalleReparacion && this.detalleReparacion.Trabajos) || []) {
        for (const it of trabajo.Items || []) {
          if (!grupos[it.Categoria]) grupos[it.Categoria] = [];
          grupos[it.Categoria].push(it);
        }
      }
      return orden
        .filter((cat) => grupos[cat] && grupos[cat].length)
        .map((cat) => ({ categoria: cat, label: labels[cat] || cat, items: grupos[cat] }));
    },

    // Autofill de sucursal según la ubicación de la unidad:
    // - "CENTRAL" -> Salta (1)
    // - "DYCAR JUJUY" -> Dycar Jujuy (2)
    // Fallback por UbicacionId para el resto de las ubicaciones.
    sucursalPorUbicacionDeUnidad(item) {
      if (!item) return null;
      const ubicacionTexto = String(item.Ubicacion || item.UbicacionDescripcion || "")
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();
      if (ubicacionTexto === "CENTRAL") return 1;
      if (ubicacionTexto === "DYCAR JUJUY") return 2;
      const ubicacionId = Number(item.UbicacionId);
      return ubicacionId === 41 ? 2 : (ubicacionId === 1 ? 1 : null);
    },
    handleAgregarConcepto({ item }) {
      if (!item) return;
      // Sucursal sugerida: la del registro (reparación o ubicación SAI de la
      // unidad), luego la ubicación de la unidad y por último el alcance del
      // usuario. Solo se sugiere una opción realmente seleccionable. La
      // sucursal es editable: al guardar se actualiza la reparación y define
      // los destinatarios de la alerta.
      const sucursalRegistro = [1, 2].includes(Number(item.SucursalId))
        ? Number(item.SucursalId)
        : null;
      const opciones = this.sucursalesTurnoDisponibles.map((sucursal) => Number(sucursal.id));
      const sucursalSugerida =
        [sucursalRegistro, this.sucursalPorUbicacionDeUnidad(item), this.sucursalAlcancePorDefecto]
          .find((numero) => numero && opciones.includes(Number(numero))) || null;
      this.abrirDialogoItem({
        titulo: "Agregar tarea",
        sucursal: sucursalSugerida,
        onConfirm: async ({ conceptos, sucursal }) => {
          let result;
          if (item.ReparacionId) {
            // Concepto(s) sobre una card existente: endpoint flat de add (lote);
            // la sucursal elegida actualiza el registro si cambió.
            result = await this.$store.dispatch("usados/agregarTrabajoReparacion", {
              id: item.ReparacionId,
              items: conceptos,
              sucursal,
            });
          } else {
            // Primer(os) concepto(s) sobre una unidad recibida: crea el registro
            // con la sucursal elegida (define los destinatarios de la alerta).
            result = await this.$store.dispatch("usados/crearReparacion", {
              UsadoID: item.UsadoID,
              Empresa: item.Empresa ?? 1,
              sucursal,
              conceptos,
            });
          }
          if (!result || result.exito !== 1) {
            this.$store.dispatch("show_snackbar", { text: result?.message || "Error", color: "error" });
            return;
          }
          const cantidad = conceptos.length;
          const mensaje = item.ReparacionId
            ? (cantidad === 1 ? "Tarea agregada" : `${cantidad} tareas agregadas`)
            : (cantidad === 1 ? "Reparación creada" : `Reparación creada con ${cantidad} tareas`);
          this.$store.dispatch("show_snackbar", { text: mensaje, color: "success" });
          await this.fetchTablero();
        },
      });
    },
    handleEditarItem({ item, trabajoId, itemId, descripcion }) {
      this.abrirDialogoTexto({
        titulo: "Editar tarea",
        valor: descripcion || "",
        onConfirm: async (texto) => {
          const result = await this.$store.dispatch("usados/editarItemReparacion", {
            id: item.ReparacionId,
            trabajoId,
            itemId,
            descripcion: texto,
          });
          if (!result || result.exito !== 1) {
            this.$store.dispatch("show_snackbar", { text: result?.message || "Error", color: "error" });
            return;
          }
          await this.fetchTablero();
        },
      });
    },
    async handleEliminarItem({ item, trabajoId, itemId }) {
      this.dialogoConfirmarEliminarMensaje = "¿Eliminar este ítem?";
      this.dialogoConfirmarEliminarCallback = async () => {
        const result = await this.$store.dispatch("usados/eliminarItemReparacion", {
          id: item.ReparacionId,
          trabajoId,
          itemId,
        });
        if (!result || result.exito !== 1) {
          this.$store.dispatch("show_snackbar", { text: result?.message || "Error", color: "error" });
          return;
        }
        await this.fetchTablero();
      };
      this.dialogoConfirmarEliminar = true;
    },
    handleVerComentarios({ item, trabajoId, itemId, descripcion }) {
      this.comentariosItemCtx = { item, trabajoId, itemId, descripcion };
      this.comentariosLista = [];
      this.comentariosNuevo = "";
      this.comentariosError = null;
      this.dialogoComentariosItem = true;
      this.cargarComentariosItem();
    },
    async cargarComentariosItem() {
      const ctx = this.comentariosItemCtx;
      if (!ctx) return;
      const requestId = ++this.comentariosRequestId;
      this.comentariosCargando = true;
      this.comentariosError = null;
      try {
        const result = await this.$store.dispatch("usados/getComentariosItemReparacion", {
          id: ctx.item.ReparacionId,
          trabajoId: ctx.trabajoId,
          itemId: ctx.itemId,
        });
        if (requestId !== this.comentariosRequestId) return;
        if (result && result.exito === 1) {
          this.comentariosLista = result.data || [];
        } else {
          this.comentariosError = result?.message || result?.msj || "Error al cargar los comentarios";
        }
      } catch (err) {
        if (requestId !== this.comentariosRequestId) return;
        this.comentariosError = err.message || "Error al cargar los comentarios";
      } finally {
        if (requestId === this.comentariosRequestId) {
          this.comentariosCargando = false;
        }
      }
    },
    async agregarComentarioItem() {
      const texto = String(this.comentariosNuevo || "").trim();
      const ctx = this.comentariosItemCtx;
      if (!texto || !ctx) return;
      this.agregandoComentario = true;
      try {
        const result = await this.$store.dispatch("usados/agregarComentarioItemReparacion", {
          id: ctx.item.ReparacionId,
          trabajoId: ctx.trabajoId,
          itemId: ctx.itemId,
          comentario: texto,
        });
        if (!result || result.exito !== 1) {
          this.$store.dispatch("show_snackbar", { text: result?.message || result?.msj || "Error", color: "error" });
          return;
        }
        this.comentariosNuevo = "";
        if (result.data) {
          this.comentariosLista.push(result.data);
        } else {
          await this.cargarComentariosItem();
        }
      } catch (err) {
        this.$store.dispatch("show_snackbar", { text: err.message || "Error al agregar el comentario", color: "error" });
      } finally {
        this.agregandoComentario = false;
      }
    },
    abrirDialogoTexto({ titulo, valor, onConfirm }) {
      this.dialogoTextoTitulo = titulo;
      this.dialogoTextoValor = valor || "";
      this.dialogoTextoCallback = onConfirm;
      this.dialogoTexto = true;
    },
    confirmarDialogoTexto() {
      const texto = String(this.dialogoTextoValor || "").trim();
      if (!texto) {
        this.$store.dispatch("show_snackbar", { text: "La descripción no puede estar vacía", color: "warning" });
        return;
      }
      const cb = this.dialogoTextoCallback;
      this.dialogoTexto = false;
      this.dialogoTextoCallback = null;
      if (cb) cb(texto);
    },
    abrirDialogoItem({ titulo, onConfirm, sucursal = null }) {
      this.dialogoItemTitulo = titulo;
      this.dialogoItemCategoria = "repuestos";
      this.dialogoItemValor = "";
      this.dialogoItemNotificar = false;
      this.dialogoItemConceptos = [];
      this.dialogoItemCallback = onConfirm;
      this.dialogoItemSucursal = sucursal;
      this.dialogoItem = true;
    },
    // Concepto del diálogo según categoría: en "mano_obra" viaja además el
    // check "Enviar notificación" que dispara la alerta de chapa y pintura.
    construirConceptoDialogoItem(descripcion) {
      const concepto = { categoria: this.dialogoItemCategoria, descripcion };
      if (this.dialogoItemCategoria === "mano_obra") {
        concepto.notificar = !!this.dialogoItemNotificar;
      }
      return concepto;
    },
    // Acumula el concepto tipeado en la lista del diálogo (carga por lote).
    agregarConceptoDialogoItem() {
      const descripcion = String(this.dialogoItemValor || "").trim();
      if (!descripcion) {
        this.$store.dispatch("show_snackbar", { text: "La descripción no puede estar vacía", color: "warning" });
        return;
      }
      this.dialogoItemConceptos.push(this.construirConceptoDialogoItem(descripcion));
      this.dialogoItemValor = "";
      this.$nextTick(() => {
        if (this.$refs.inputConceptoDialogoItem) this.$refs.inputConceptoDialogoItem.focus();
      });
    },
    quitarConceptoDialogoItem(idx) {
      this.dialogoItemConceptos.splice(idx, 1);
    },
    confirmarDialogoItem() {
      const conceptos = [...this.dialogoItemConceptos];
      const descripcion = String(this.dialogoItemValor || "").trim();
      if (descripcion) {
        conceptos.push(this.construirConceptoDialogoItem(descripcion));
      }
      if (!conceptos.length) {
        this.$store.dispatch("show_snackbar", { text: "Agregá al menos una tarea", color: "warning" });
        return;
      }
      if (!this.dialogoItemSucursal) {
        this.$store.dispatch("show_snackbar", { text: "Seleccioná la sucursal", color: "warning" });
        return;
      }
      const cb = this.dialogoItemCallback;
      const sucursal = this.dialogoItemSucursal;
      this.dialogoItem = false;
      this.dialogoItemConceptos = [];
      this.dialogoItemCallback = null;
      if (cb) cb({ conceptos, sucursal });
    },
    async confirmarEliminacion() {
      const cb = this.dialogoConfirmarEliminarCallback;
      this.dialogoConfirmarEliminar = false;
      this.dialogoConfirmarEliminarCallback = null;
      if (cb) await cb();
    },

    claveItemEnProceso(item, itemId) {
      if (!item || itemId === null || itemId === undefined) return null;
      const tieneReparacion = item.ReparacionId !== null && item.ReparacionId !== undefined && item.ReparacionId !== "";
      const entidad = tieneReparacion ? "reparacion" : "unidad";
      const entidadId = tieneReparacion ? item.ReparacionId : this.claveUnidad(item);
      const proyeccion = item.Proyeccion || "recibidos";
      if (entidadId === null || entidadId === undefined || entidadId === "") return null;
      return `${entidad}:${entidadId}|proyeccion:${proyeccion}|item:${itemId}`;
    },

    async handleCompletarItem({ item, trabajoId, itemId, Completado, Categoria }) {
      const clave = this.claveItemEnProceso(item, itemId);
      // Solo se descarta un segundo click sobre el MISMO ítem; otros ítems
      // pueden confirmarse en paralelo sin quedar en espera.
      if (clave && this.itemsEnProceso.indexOf(clave) !== -1) return;
      if (clave) this.itemsEnProceso = [...this.itemsEnProceso, clave];
      try {
        const result = await this.$store.dispatch("usados/completarItemReparacion", {
          id: item.ReparacionId,
          trabajoId,
          itemId,
          Completado,
        });
        if (!result || result.exito !== 1) {
          this.$store.dispatch("show_snackbar", {
            text: result?.message || "Error al confirmar el ítem",
            color: "error",
          });
          await this.fetchTablero();
          return;
        }
        const tableroResult = await this.fetchTablero();
        if (Completado && result.data) {
          if (result.data.RepuestosCompletos && this.puedeGestionar && Categoria === "repuestos") {
            // La proyección de repuestos solo contiene ítems de esa categoría.
            // Usar la card recién recargada permite incluir la mano de obra
            // automática que el backend acaba de insertar.
            this.reparacionAAgendarPrompt = this.buscarReparacionEnTablero(
              tableroResult?.data,
              item.ReparacionId
            ) || item;
            this.dialogoAgendarPrompt = true;
          } else if (result.data.UnidadCompleta && this.puedeGestionar) {
            // La unidad llegó al 100%: abrir la confirmación de finalización
            // desde cualquier columna (el estado real puede ser espera_repuestos
            // o agendado). Usar la card recargada mantiene los datos frescos.
            this.reparacionAFinalizar = this.buscarReparacionEnTablero(
              tableroResult?.data,
              item.ReparacionId
            ) || item;
            this.dialogoConfirmarFinalizar = true;
          }
        }
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al confirmar: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        if (clave) {
          this.itemsEnProceso = this.itemsEnProceso.filter((k) => k !== clave);
        }
      }
    },

    async handleCrearAplicacion({ item, trabajoId, itemId }) {
      const clave = this.claveItemEnProceso(item, itemId);
      if (clave && this.aplicacionesEnProceso.indexOf(clave) !== -1) return;
      if (clave) this.aplicacionesEnProceso = [...this.aplicacionesEnProceso, clave];
      try {
        const result = await this.$store.dispatch("usados/crearAplicacionRepuesto", {
          id: item.ReparacionId,
          trabajoId,
          itemId,
        });
        if (!result || result.exito !== 1) {
          this.$store.dispatch("show_snackbar", {
            text: result?.message || result?.msj || "Error al crear la aplicación",
            color: "error",
          });
          return;
        }
        await this.fetchTablero();
        this.$store.dispatch("show_snackbar", {
          text: result.data?.Creada ? "Aplicación creada" : "La aplicación ya estaba creada",
          color: "success",
        });
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al crear la aplicación: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        if (clave) {
          this.aplicacionesEnProceso = this.aplicacionesEnProceso.filter((k) => k !== clave);
        }
      }
    },

    toggleFiltroUnidad(item) {
      if (!item || !item.UsadoID) return;
      const clave = this.claveUnidad(item);
      this.unidadFiltradaId = this.unidadFiltradaId === clave
        ? null
        : clave;
    },

    scrollToMobileColumn(estado, index) {
      this.mobileColumnTab = index;
      this.$nextTick(() => {
        const tablero = this.$refs.tableroReparaciones;
        if (tablero && typeof tablero.scrollToColumn === "function") {
          tablero.scrollToColumn(estado);
        }
      });
    },

    // ---------------------------------------------------------------------
    // Agendamiento + finalización
    // ---------------------------------------------------------------------
    async cargarDisponibilidad({ fecha, item, turnoIdExcluir = null, sucursal, recepcionistaId, tiempoEstimado }) {
      if (!fecha || !sucursal || !recepcionistaId || !this.tiempoManoObraValido(tiempoEstimado)) {
        return [];
      }
      const result = await this.$store.dispatch("usados/getDisponibilidadReparacion", {
        fecha,
        id: item && item.ReparacionId,
        usadoId: item && item.UsadoID,
        turnoIdExcluir,
        sucursal,
        recepcionistaId,
        tiempoEstimado,
      });
      if (!result || result.exito !== 1) {
        this.$store.dispatch("show_snackbar", {
          text: result?.message || result?.msj || "No se pudo consultar disponibilidad",
          color: "warning",
        });
        return [];
      }
      return result.data.horariosDisponibles || [];
    },
    async cargarRecepcionistas(sucursal) {
      const requestId = ++this.recepcionistasRequestId;
      this.cargandoRecepcionistas = true;
      try {
        const result = await this.$store.dispatch("usados/getRecepcionistasReparacion", { sucursal });
        const recepcionistas = result && result.exito === 1 ? (result.data || []) : [];
        if (requestId === this.recepcionistasRequestId) {
          this.recepcionistas = recepcionistas;
        }
        if (!result || result.exito !== 1) {
          this.$store.dispatch("show_snackbar", {
            text: result?.message || result?.msj || "No se pudieron consultar los recepcionistas",
            color: "warning",
          });
        }
        return recepcionistas;
      } finally {
        if (requestId === this.recepcionistasRequestId) {
          this.cargandoRecepcionistas = false;
        }
      }
    },
    async cargarDisponibilidadGestion(fecha) {
      if (!this.reparacionAAgendar) return;
      const requestId = ++this.disponibilidadRequestId;
      this.cargandoDisponibilidad = true;
      try {
        const horarios = await this.cargarDisponibilidad({
          fecha,
          item: this.reparacionAAgendar,
          turnoIdExcluir: this.turnoSeleccionado && this.turnoSeleccionado.TurnoId,
          sucursal: this.agendarForm.sucursal,
          recepcionistaId: this.agendarForm.recepcionistaId,
          tiempoEstimado: this.agendarForm.tiempoEstimado,
        });
        if (requestId === this.disponibilidadRequestId) this.horariosDisponibles = horarios;
      } finally {
        if (requestId === this.disponibilidadRequestId) this.cargandoDisponibilidad = false;
      }
    },
    refrescarDisponibilidadGestion() {
      this.horariosDisponibles = [];
      this.disponibilidadRequestId += 1;
      this.cargandoDisponibilidad = false;
      if (this.disponibilidadGestionCompleta) {
        this.cargarDisponibilidadGestion(this.agendarForm.fecha);
      }
    },
    abrirGestionTurnos(item) {
      const mismoVehiculo = Number(this.reparacionAAgendar?.ReparacionId) === Number(item?.ReparacionId);
      const reparacionesDisponibles = [this.recibidos, this.espera_repuestos, this.agendado, this.terceros, this.en_proceso]
        .reduce((todas, reparaciones) => todas.concat(reparaciones || []), []);
      const itemCompleto = item?.ReparacionId
        ? reparacionesDisponibles.find((candidato) => Number(candidato.ReparacionId) === Number(item.ReparacionId) && (candidato.Trabajos || []).length)
        : null;
      this.reparacionAAgendar = itemCompleto || item;
      if (!mismoVehiculo) this.seleccionadosTurno = [];
      this.prepararNuevoTurno();
      this.dialogoAgendar = true;
    },
    chipSeleccionadoTurno(chip) {
      return this.seleccionadosTurno.includes(chip.key);
    },
    toggleChipTurno(chip) {
      if (this.reprogramando) return;
      const index = this.seleccionadosTurno.indexOf(chip.key);
      if (index >= 0) {
        this.seleccionadosTurno.splice(index, 1);
        return;
      }
      if (this.seleccionadosTurno.length >= 2) {
        this.$store.dispatch("show_snackbar", {
          text: "Podés seleccionar como máximo dos tareas",
          color: "warning",
        });
        return;
      }
      this.seleccionadosTurno.push(chip.key);
    },
    seleccionPayloadTurno() {
      const seleccionados = this.chipsTurno.filter((chip) => this.seleccionadosTurno.includes(chip.key));
      const ids = (chips, propiedad) => chips.reduce(
        (resultado, chip) => resultado.concat(chip[propiedad] || []),
        []
      );
      return {
        trabajoIds: [...new Set(ids(seleccionados, "trabajoIds"))],
        manoObraItemIds: [...new Set(ids(seleccionados, "manoObraItemIds"))],
      };
    },
    confirmarAgendarPrompt() {
      const item = this.reparacionAAgendarPrompt;
      this.dialogoAgendarPrompt = false;
      this.reparacionAAgendarPrompt = null;
      if (item) {
        this.abrirGestionTurnos(item);
      }
    },
    prepararNuevoTurno() {
      this.reprogramando = false;
      this.turnoSeleccionado = null;
      const sucursal = this.estaScopeadoPorSucursal
        ? this.sucursalAlcancePorDefecto
        : this.sucursalPorUbicacionDeUnidad(this.reparacionAAgendar);
      this.agendarForm = {
        fecha: "",
        hora: "",
        sucursal,
        recepcionistaId: null,
        tiempoEstimado: 1,
      };
      this.horariosDisponibles = [];
    },
    async seleccionarTurnoParaReprogramar(turno) {
      this.reprogramando = true;
      this.turnoSeleccionado = turno;
      const fecha = turno && turno.FechaTurno ? String(turno.FechaTurno).slice(0, 10) : "";
      const sucursal = this.estaScopeadoPorSucursal
        ? this.sucursalAlcancePorDefecto
        : (Number(turno && turno.SucursalNumero) || null);
      this.agendarForm = {
        fecha,
        hora: turno && turno.Hora ? String(turno.Hora).slice(0, 5) : "",
        sucursal,
        recepcionistaId: null,
        tiempoEstimado: Number(turno && turno.TiempoEstimado) || 1,
      };
      if (sucursal) {
        const recepcionistas = await this.cargarRecepcionistas(sucursal);
        const recepcionistaId = Number(turno && turno.RecepcionistaId) || null;
        this.agendarForm.recepcionistaId = recepcionistas.some((item) => Number(item.id) === recepcionistaId)
          ? recepcionistaId
          : null;
      }
    },
    handleAgendar(item) {
      this.abrirGestionTurnos(item);
    },
    handleReprogramar(item) {
      this.abrirGestionTurnos(item);
    },
    async confirmarAgendar() {
      if (!this.reparacionAAgendar) return;
      if (!this.reprogramando && !this.seleccionTurnoValida) {
        this.$store.dispatch("show_snackbar", {
          text: "Seleccioná al menos una tarea para poder agendar",
          color: "warning",
        });
        return;
      }
      const { fecha, hora, sucursal, recepcionistaId, tiempoEstimado } = this.agendarForm;
      if (!sucursal || !recepcionistaId || !this.tiempoManoObraValido(tiempoEstimado) || !fecha || !hora) {
        this.$store.dispatch("show_snackbar", { text: "Completá sucursal, recepcionista, tiempo estimado, fecha y horario", color: "warning" });
        return;
      }
      this.agendando = true;
      try {
        const action = this.reprogramando ? "usados/reprogramarReparacion" : "usados/agendarReparacion";
        const payload = {
          id: this.reparacionAAgendar.ReparacionId,
          reparacionTurnoId: this.turnoSeleccionado && this.turnoSeleccionado.Id,
          fecha,
          hora,
          sucursal,
          recepcionistaId,
          tiempoEstimado: Number(tiempoEstimado),
        };
        if (!this.reprogramando) Object.assign(payload, this.seleccionPayloadTurno());
        const result = await this.$store.dispatch(action, payload);
        if (!result || result.exito !== 1) {
          if (result?.code === "SLOT_UNAVAILABLE") {
            this.agendarForm.hora = "";
            this.refrescarDisponibilidadGestion();
          }
          this.$store.dispatch("show_snackbar", {
            text: result?.code === "SLOT_UNAVAILABLE"
              ? "El horario dejó de estar disponible. La disponibilidad fue actualizada."
              : (result?.message || "Error al agendar"),
            color: "error",
          });
          return;
        }
        this.dialogoAgendar = false;
        this.$store.dispatch("show_snackbar", {
          text: this.reprogramando ? "Turno reprogramado" : "Turno agendado",
          color: "success",
        });
        await this.fetchTablero();
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al agendar: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        this.agendando = false;
      }
    },

    handleCancelar(item) {
      this.reparacionACancelar = item;
      this.dialogoConfirmarCancelar = true;
    },
    handleCancelarTurno(turno) {
      this.reparacionACancelar = { ...this.reparacionAAgendar, TurnoSeleccionado: turno };
      this.dialogoConfirmarCancelar = true;
    },
    async confirmarCancelar() {
      if (!this.reparacionACancelar) return;
      this.cancelando = true;
      try {
        const result = await this.$store.dispatch("usados/cancelarReparacion", {
          id: this.reparacionACancelar.ReparacionId,
          reparacionTurnoId: this.reparacionACancelar.TurnoSeleccionado && this.reparacionACancelar.TurnoSeleccionado.Id,
        });
        if (!result || result.exito !== 1) {
          this.$store.dispatch("show_snackbar", {
            text: result?.message || "Error al cancelar",
            color: "error",
          });
          return;
        }
        this.dialogoConfirmarCancelar = false;
        this.dialogoAgendar = false;
        this.$store.dispatch("show_snackbar", { text: "Turno cancelado", color: "success" });
        await this.fetchTablero();
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al cancelar: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        this.cancelando = false;
      }
    },

    handleFinalizar(item) {
      this.reparacionAFinalizar = item;
      this.dialogoConfirmarFinalizar = true;
    },
    cancelarFinalizacion() {
      this.dialogoConfirmarFinalizar = false;
      this.reparacionAFinalizar = null;
    },
    async confirmarFinalizacion() {
      if (!this.reparacionAFinalizar) return;
      const reparacion = this.reparacionAFinalizar;
      this.finalizando = true;
      try {
        const result = await this.$store.dispatch("usados/finalizarReparacion", {
          id: reparacion.ReparacionId,
        });
        if (!result || result.exito !== 1) {
          this.$store.dispatch("show_snackbar", {
            text: result?.message || "Error al finalizar",
            color: "error",
          });
          return;
        }
        this.dialogoConfirmarFinalizar = false;
        if (this.unidadFiltradaId === this.claveUnidad(reparacion)) {
          this.unidadFiltradaId = null;
        }
        this.$store.dispatch("show_snackbar", { text: "Reparación finalizada", color: "success" });
        await this.fetchTablero();
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al finalizar: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        this.finalizando = false;
        this.reparacionAFinalizar = null;
      }
    },

    async handleReenviarEmail(item) {
      try {
        const result = await this.$store.dispatch("usados/reenviarEmailReparacion", {
          id: item.ReparacionId,
        });
        if (!result || result.exito !== 1) {
          this.$store.dispatch("show_snackbar", {
            text: result?.message || "Error al reenviar",
            color: "error",
          });
          return;
        }
        this.$store.dispatch("show_snackbar", {
          text: `Email reenviado (${result.data?.enviados ?? 0} destinatarios)`,
          color: "success",
        });
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al reenviar: " + (err.message || "Error desconocido"),
          color: "error",
        });
      }
    },

    // ---------------------------------------------------------------------
    // ABM de destinatarios de alertas
    // ---------------------------------------------------------------------
    validarEmail(valor) {
      const email = String(valor || "").trim().toLowerCase();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    async abrirGestionDestinatarios() {
      if (!this.permisos.destinatarios) return;
      if (!this.sucursales || this.sucursales.length === 0) {
        await this.$store.dispatch("genericos/get_sucursales");
      }
      if (
        !this.destinatarioSucursalNumero &&
        this.sucursalesAgendables.length > 0
      ) {
        this.destinatarioSucursalNumero = this.sucursalesAgendables[0].id;
      }
      this.dialogoDestinatarios = true;
      await this.cargarDestinatarios();
    },
    async cargarDestinatarios() {
      this.cargandoDestinatarios = true;
      try {
        const payload = {
          tipo_alerta: this.destinatarioTipoAlerta,
        };
        if (this.destinatarioSucursalNumero) {
          payload.sucursal_numero = this.destinatarioSucursalNumero;
        }
        const res = await this.$store.dispatch(
          "usados/getDestinatariosReparacion",
          payload
        );
        if (res.exito) {
          this.destinatarios = Array.isArray(res.data) ? res.data : [];
        } else {
          this.$store.dispatch("show_snackbar", {
            text: res.message || "No se pudieron cargar los destinatarios.",
            color: "error",
          });
        }
      } finally {
        this.cargandoDestinatarios = false;
      }
    },
    async crearDestinatario() {
      if (
        !this.destinatarioSucursalNumero ||
        !this.destinatarioTipoAlerta ||
        !this.destinatarioNuevoNombre ||
        !this.validarEmail(this.destinatarioNuevoEmail)
      ) {
        this.$store.dispatch("show_snackbar", {
          text: "Debe indicar sucursal, tipo de alerta, un nombre y un email válido.",
          color: "warning",
        });
        return;
      }
      this.guardandoDestinatario = true;
      try {
        const res = await this.$store.dispatch(
          "usados/altaDestinatarioReparacion",
          {
            sucursal_numero: this.destinatarioSucursalNumero,
            tipo_alerta: this.destinatarioTipoAlerta,
            nombre: this.destinatarioNuevoNombre,
            email: this.destinatarioNuevoEmail,
            telefono: this.destinatarioNuevoTelefono || null,
          }
        );
        if (res.exito) {
          this.destinatarioNuevoEmail = "";
          this.destinatarioNuevoTelefono = "";
          this.destinatarioNuevoNombre = "";
          await this.cargarDestinatarios();
          this.$store.dispatch("show_snackbar", {
            text: res.message || "Destinatario dado de alta.",
            color: "success",
          });
        } else {
          this.$store.dispatch("show_snackbar", {
            text: res.message || "No se pudo crear el destinatario.",
            color: "error",
          });
        }
      } finally {
        this.guardandoDestinatario = false;
      }
    },
    abrirEditarDestinatario(item) {
      this.destinatarioEditar = {
        id: item.id,
        sucursal_numero: item.sucursal_numero,
        tipo_alerta: item.tipo_alerta || "repuestos_completos",
        email: item.email || "",
        telefono: item.telefono || "",
        nombre: item.nombre || "",
      };
      this.dialogoEditarDestinatario = true;
    },
    cerrarEditarDestinatario() {
      this.dialogoEditarDestinatario = false;
      this.destinatarioEditar = {
        id: null,
        sucursal_numero: null,
        tipo_alerta: this.destinatarioTipoAlerta || "repuestos_completos",
        email: "",
        telefono: "",
        nombre: "",
      };
    },
    async guardarEdicionDestinatario() {
      if (
        !this.destinatarioEditar.id ||
        !this.destinatarioEditar.sucursal_numero ||
        !this.destinatarioEditar.tipo_alerta ||
        !(this.destinatarioEditar.nombre || "").trim() ||
        !this.validarEmail(this.destinatarioEditar.email)
      ) {
        this.$store.dispatch("show_snackbar", {
          text: "Debe indicar sucursal, tipo de alerta, un nombre y un email válido.",
          color: "warning",
        });
        return;
      }
      this.guardandoEdicionDestinatario = true;
      try {
        const res = await this.$store.dispatch(
          "usados/editarDestinatarioReparacion",
          this.destinatarioEditar
        );
        if (res.exito) {
          this.cerrarEditarDestinatario();
          await this.cargarDestinatarios();
          this.$store.dispatch("show_snackbar", {
            text: res.message || "Destinatario actualizado con éxito.",
            color: "success",
          });
        } else {
          this.$store.dispatch("show_snackbar", {
            text: res.message || "No se pudo actualizar el destinatario.",
            color: "error",
          });
        }
      } finally {
        this.guardandoEdicionDestinatario = false;
      }
    },
    async darBajaDestinatario(item) {
      this.destinatarioBajandoId = item.id;
      try {
        const res = await this.$store.dispatch(
          "usados/bajaDestinatarioReparacion",
          { id: item.id }
        );
        if (res.exito) {
          await this.cargarDestinatarios();
          this.$store.dispatch("show_snackbar", {
            text: res.message || "Destinatario dado de baja.",
            color: "success",
          });
        } else {
          this.$store.dispatch("show_snackbar", {
            text: res.message || "No se pudo dar de baja el destinatario.",
            color: "error",
          });
        }
      } finally {
        this.destinatarioBajandoId = null;
      }
    },
    async darAltaDestinatario(item) {
      this.destinatarioAltaId = item.id;
      try {
        const res = await this.$store.dispatch(
          "usados/reactivarDestinatarioReparacion",
          { id: item.id }
        );
        if (res.exito) {
          await this.cargarDestinatarios();
          this.$store.dispatch("show_snackbar", {
            text: res.message || "Destinatario dado de alta.",
            color: "success",
          });
        } else {
          this.$store.dispatch("show_snackbar", {
            text: res.message || "No se pudo dar de alta el destinatario.",
            color: "error",
          });
        }
      } finally {
        this.destinatarioAltaId = null;
      }
    },
    tipoAlertaDestinatarioTexto(tipo) {
      const hit = this.tiposDestinatarioAlertaOptions.find(
        (item) => item.value === tipo
      );
      return hit ? hit.text : tipo || "—";
    },
    sucursalDestinatarioTexto(numero) {
      const hit = (this.sucursales || []).find(
        (s) => Number(s.id) === Number(numero)
      );
      return hit ? hit.provincia : `Sucursal ${numero}`;
    },
    formatearTelefono(telefono) {
      if (!telefono) return "";
      const str = String(telefono);
      if (str.length >= 10) {
        return str.replace(/(\d{2,4})(\d{3,4})(\d{4})/, "$1 $2 $3");
      }
      return str;
    },

    handleLimpiar(item) {
      this.reparacionALimpiar = item;
      this.dialogoConfirmarLimpiar = true;
    },
    async confirmarLimpiar() {
      if (!this.reparacionALimpiar) return;
      this.limpiando = true;
      try {
        const result = await this.$store.dispatch("usados/limpiarReparacion", {
          id: this.reparacionALimpiar.ReparacionId,
        });
        if (!result || result.exito !== 1) {
          this.$store.dispatch("show_snackbar", {
            text: result?.message || "Error al limpiar",
            color: "error",
          });
          return;
        }
        this.dialogoConfirmarLimpiar = false;
        this.$store.dispatch("show_snackbar", { text: "Reparación limpiada", color: "success" });
        await this.fetchTablero();
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al limpiar: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        this.limpiando = false;
        this.reparacionALimpiar = null;
      }
    },

    verDetalle(item) {
      this.detalleReparacion = item;
      this.dialogoDetalle = true;
    },
    async abrirCostoTaller(item) {
      if (!item || !item.ReparacionId) return;
      const requestId = ++this.costoTallerRequestId;
      this.costoTallerUnidad = item;
      this.costoTallerLineas = [];
      this.costoTallerTotal = null;
      this.costoTallerFiltroDesde = null;
      this.costoTallerFiltroHasta = null;
      this.costoTallerError = null;
      this.costoTallerCargando = true;
      this.dialogoCostoTaller = true;
      try {
        const result = await this.$store.dispatch("usados/obtenerCostoTaller", { id: item.ReparacionId });
        // Si se abrió/cerró otra consulta mientras cargaba, esta respuesta es obsoleta.
        if (requestId !== this.costoTallerRequestId) return;
        if (!result || result.exito !== 1) {
          this.costoTallerError = result?.message || result?.msj || "No se pudo cargar el detalle del costo de taller.";
          return;
        }
        this.costoTallerTotal = result.data?.CostoTaller ?? 0;
        this.costoTallerLineas = result.data?.Lineas || [];
      } catch (error) {
        if (requestId === this.costoTallerRequestId) {
          this.costoTallerError = error.message || "No se pudo cargar el detalle del costo de taller.";
        }
      } finally {
        if (requestId === this.costoTallerRequestId) this.costoTallerCargando = false;
      }
    },
    onCostoTallerDialogChange(abierto) {
      if (abierto) return;
      this.costoTallerRequestId += 1;
      this.costoTallerCargando = false;
      this.costoTallerError = null;
    },
    // Convierte una fecha DD/MM/YYYY a un entero comparable (AAAAMMDD);
    // devuelve null si la fecha no tiene el formato esperado.
    fechaCostoTallerANumero(fecha) {
      if (!fecha || typeof fecha !== "string") return null;
      const partes = fecha.split("/");
      if (partes.length !== 3) return null;
      const dia = parseInt(partes[0], 10);
      const mes = parseInt(partes[1], 10);
      const anio = parseInt(partes[2], 10);
      if (!dia || !mes || !anio) return null;
      return anio * 10000 + mes * 100 + dia;
    },
    limpiarFiltroCostoTaller() {
      this.costoTallerFiltroDesde = null;
      this.costoTallerFiltroHasta = null;
    },
    handleVerDetalleOrden({ item, ordenes, anteriores, anterioresTotal }) {
      this.detalleOrdenUnidad = item;
      this.ordenesDetalle = ordenes || [];
      this.ordenesAnterioresDetalle = anteriores || [];
      this.ordenesAnterioresTotalDetalle = Number(anterioresTotal || 0);
      this.dialogoDetalleOrden = true;
    },

    // ---------------------------------------------------------------------
    // Drag & drop
    // ---------------------------------------------------------------------
    handleDragChange({ item, fromEstado, toEstado }) {
      // En v2 no hay transiciones por drag: la única transición drag es
      // Recibidos -> En espera (alta). El resto se gestiona con botones.
      if (fromEstado === "recibidos" && toEstado === "espera_repuestos") {
        this.reparacionAAgendar = null;
        this.abrirNuevaReparacion();
        if (item && item.UsadoID) {
          this.nuevaReparacion.ClaveUnidad = this.claveUnidad(item);
        }
        this.$store.dispatch("show_snackbar", {
          text: "Completá las tareas para crear la reparación",
          color: "info",
        });
        return;
      }
      this.$store.dispatch("show_snackbar", {
        text: "Usá los botones de la tarjeta para cambiar de estado",
        color: "warning",
      });
      this.fetchTablero();
    },

    // ---------------------------------------------------------------------
    // Peritaje
    // ---------------------------------------------------------------------
    handleCargarPeritaje(item) {
      this.peritajeUnidad = item;
      this.peritajeArchivos = [];
      this.peritajeError = null;
      this.dialogoPeritaje = true;
    },
    async confirmarSubirPeritaje() {
      if (!this.peritajeArchivos.length) return;
      const archivo = this.peritajeArchivos[0];

      this.subiendoPeritaje = true;
      this.peritajeError = null;
      try {
        // Convertir archivo a base64 usando getBase64 del util
        const { getBase64 } = await import("../../util/utils");
        const base64 = await getBase64(archivo);

        const item = this.peritajeUnidad;
        const payload = {
          id: item.ReparacionId || 0,
          Empresa: item.Empresa ?? 1,
          archivo: base64,
          nombreArchivo: archivo.name,
        };
        if (!item.ReparacionId) {
          payload.usadoId = item.UsadoID;
        }

        const result = await this.$store.dispatch("usados/subirPeritaje", payload);
        if (!result || result.exito !== 1) {
          this.peritajeError = result?.message || "Error al subir el peritaje";
          return;
        }

        this.dialogoPeritaje = false;
        this.$store.dispatch("show_snackbar", { text: "Peritaje guardado correctamente", color: "success" });
        await this.fetchTablero();
      } catch (err) {
        this.peritajeError = "Error al subir: " + (err.message || "Error desconocido");
      } finally {
        this.subiendoPeritaje = false;
      }
    },
    async handleVerPeritaje(item) {
      this.verPeritajeUnidad = item;
      this.verPeritajeURL = null;
      this.verPeritajeCargando = true;
      this.dialogoVerPeritaje = true;
      try {
        const result = await this.$store.dispatch("usados/obtenerPeritaje", {
          id: item.ReparacionId,
        });
        if (result && result.exito === 1 && result.data?.PeritajeURL) {
          this.verPeritajeURL = result.data.PeritajeURL;
        } else {
          this.$store.dispatch("show_snackbar", {
            text: result?.message || "No se pudo cargar el peritaje",
            color: "warning",
          });
        }
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al cargar peritaje: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        this.verPeritajeCargando = false;
      }
    },
    handleEditarPeritaje() {
      this.dialogoVerPeritaje = false;
      if (this.verPeritajeUnidad) {
        this.handleCargarPeritaje(this.verPeritajeUnidad);
      }
    },
    async handleEliminarPeritaje() {
      if (!this.verPeritajeUnidad) return;
      this.eliminandoPeritaje = true;
      try {
        const result = await this.$store.dispatch("usados/eliminarPeritaje", {
          id: this.verPeritajeUnidad.ReparacionId,
        });
        if (!result || result.exito !== 1) {
          this.$store.dispatch("show_snackbar", {
            text: result?.message || "Error al eliminar el peritaje",
            color: "error",
          });
          return;
        }
        this.dialogoVerPeritaje = false;
        this.$store.dispatch("show_snackbar", { text: "Peritaje eliminado", color: "success" });
        await this.fetchTablero();
      } catch (err) {
        this.$store.dispatch("show_snackbar", {
          text: "Error al eliminar: " + (err.message || "Error desconocido"),
          color: "error",
        });
      } finally {
        this.eliminandoPeritaje = false;
      }
    },
    descargarPeritaje() {
      if (!this.verPeritajeURL) return;
      const link = document.createElement("a");
      link.href = this.verPeritajeURL;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // ---------------------------------------------------------------------
    // Deep link + polling
    // ---------------------------------------------------------------------
    manejarDeepLink() {
      const reparacionId = this.$route && this.$route.query && this.$route.query.reparacionId;
      if (!reparacionId) return;
      this.quickSearch = "";
      this.$nextTick(() => {
        const objetivo = [this.recibidos, this.espera_repuestos, this.agendado, this.terceros, this.en_proceso, this.finalizados]
          .flat()
          .find((c) => String(c.ReparacionId) === String(reparacionId));
        if (!objetivo) return;
        if (objetivo.Estado === "finalizado") {
          this.verDetalle(objetivo);
          return;
        }
        this.scrollToCard(objetivo);
      });
    },
    scrollToCard(card) {
      const tablero = this.$refs.tableroReparaciones;
      if (!tablero) return;
      const key = tablero.cardKey(card);
      const columnaEstado = {
        espera_repuestos: "espera_repuestos",
        agendado: "agendado",
        en_proceso: "en_proceso",
        recibido: "recibidos",
      }[card.Estado];
      if (columnaEstado) {
        this.$nextTick(() => {
          if (tablero && typeof tablero.scrollToColumn === "function") {
            tablero.scrollToColumn(columnaEstado);
          }
        });
      }
    },
    iniciarPolling() {
      this.detenerPolling();
      this.pollTimer = setInterval(() => {
        this.fetchTablero();
      }, 45000);
    },
    detenerPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    // ---------------------------------------------------------------------
    // Mobile: el botón "atrás" cierra el diálogo que está más arriba
    // ---------------------------------------------------------------------

    /**
     * Cierra el diálogo más anidado que esté abierto (el que se ve arriba).
     * Se llama desde el handler de popstate del botón "atrás" del celular.
     */
    _closeTopDialog() {
      if (this.dialogoEditarDestinatario) {
        this.cerrarEditarDestinatario();
        return;
      }
      if (this.dialogoConfirmarEliminar) {
        this.dialogoConfirmarEliminar = false;
        return;
      }
      if (this.dialogoTexto) {
        this.dialogoTexto = false;
        return;
      }
      if (this.dialogoItem) {
        this.dialogoItem = false;
        return;
      }
      if (this.dialogoConfirmarFinalizar) {
        this.cancelarFinalizacion();
        return;
      }
      if (this.dialogoConfirmarCancelar) {
        this.dialogoConfirmarCancelar = false;
        return;
      }
      if (this.dialogoConfirmarLimpiar) {
        this.dialogoConfirmarLimpiar = false;
        return;
      }
      if (this.dialogoAgendarPrompt) {
        this.dialogoAgendarPrompt = false;
        return;
      }
      if (this.dialogoAgendar) {
        this.dialogoAgendar = false;
        return;
      }
      if (this.dialogoNuevaReparacion) {
        this.dialogoNuevaReparacion = false;
        return;
      }
      if (this.dialogoPeritaje) {
        this.dialogoPeritaje = false;
        return;
      }
      if (this.dialogoVerPeritaje) {
        this.dialogoVerPeritaje = false;
        return;
      }
      if (this.dialogoDetalleOrden) {
        this.dialogoDetalleOrden = false;
        return;
      }
      if (this.dialogoDetalle) {
        this.dialogoDetalle = false;
        return;
      }
      if (this.dialogoCostoTaller) {
        this.dialogoCostoTaller = false;
        return;
      }
      if (this.dialogoDestinatarios) {
        this.dialogoDestinatarios = false;
        return;
      }
      if (this.dialogoHistorial) {
        this.cerrarHistorial();
      }
    },

    /**
     * Empuja una entrada fantasma en el historial y registra el listener de
     * popstate: así el botón "atrás" del celular cierra el diálogo abierto
     * en vez de navegar fuera de la página.
     */
    _pushDialogHistory() {
      if (this._popStateDialogHandler) return;
      this._popStateDialogHandler = () => {
        // El "atrás" consumió la entrada fantasma.
        this._dialogHistoryPushed = false;
        this._closeTopDialog();
        // Si quedó otro diálogo abierto, se vuelve a empujar para
        // que el próximo "atrás" siga cerrando diálogos.
        if (this.anyDialogOpen) {
          window.history.pushState(null, "", window.location.href);
          this._dialogHistoryPushed = true;
        }
      };
      window.history.pushState(null, "", window.location.href);
      this._dialogHistoryPushed = true;
      window.addEventListener("popstate", this._popStateDialogHandler);
    },

    /**
     * Quita el listener de popstate cuando ya no hay diálogos abiertos.
     * Si la entrada fantasma quedó sin consumir (el diálogo se cerró con la X),
     * la descarta para que un solo "atrás" salga de la página.
     */
    _removeDialogHistory(desmontando = false) {
      if (!this._popStateDialogHandler) return;
      window.removeEventListener("popstate", this._popStateDialogHandler);
      this._popStateDialogHandler = null;
      if (this._dialogHistoryPushed && !desmontando) {
        this._dialogHistoryPushed = false;
        window.history.back();
      }
    },

    /**
     * Sincroniza el historial con la presencia de diálogos.
     * Solo se activa en mobile; en desktop no interfiere.
     */
    _syncDialogHistory(shouldListen) {
      if (shouldListen && this.esMobile) {
        this._pushDialogHistory();
      } else {
        this._removeDialogHistory();
      }
    },
  },
  mounted() {
    this.fetchTablero();
    this.manejarDeepLink();
    this.iniciarPolling();
  },
  beforeDestroy() {
    this.detenerPolling();
    // Al desmontar no se revierte el historial: el usuario puede estar navegando
    // a otra ruta y un history.back() lo devolvería a esta pantalla.
    this._removeDialogHistory(true);
  },
};
</script>

<style scoped>
.reparaciones-kanban {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.reparaciones-toolbar {
  flex-shrink: 0;
}

.reparaciones-toolbar-actions {
  gap: 8px 0;
}

.reparaciones-board-row {
  flex: 1;
  min-height: 0;
}

.reparaciones-board-col {
  height: 100%;
}

.reparaciones-board {
  height: 100%;
}

.reparaciones-mobile-tabs {
  display: none;
}

.reparaciones-finalizados-search {
  max-width: 300px;
}

.reparaciones-dialog-title {
  white-space: normal;
  overflow-wrap: anywhere;
}

/* Con botón de cierre: el texto es el que se ajusta y el botón queda en la primera fila, a la derecha. */
.reparaciones-dialog-title--closable {
  flex-wrap: nowrap;
}

.reparaciones-dialog-title-text {
  flex: 1 1 auto;
  min-width: 0;
}

.reparaciones-dialog-actions {
  flex-wrap: wrap;
  gap: 4px 0;
}

.historial-dialog {
  min-height: 100vh;
}

.historial-content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
}

.historial-state,
.historial-layout {
  width: 100%;
}

.historial-state {
  margin: auto;
}

.historial-layout {
  margin-block: auto;
}

.historial-navigation {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-rows: auto minmax(0, 1fr) auto;
  grid-template-areas:
    ". arriba ."
    "anterior timeline siguiente"
    ". abajo .";
  align-items: center;
  gap: 8px 16px;
  width: 100%;
}

.historial-control-wrap {
  display: flex;
}

.historial-control-wrap--arriba {
  grid-area: arriba;
  justify-self: center;
}

.historial-control-wrap--anterior {
  grid-area: anterior;
}

.historial-control-wrap--siguiente {
  grid-area: siguiente;
}

.historial-control-wrap--abajo {
  grid-area: abajo;
  justify-self: center;
}

.historial-control {
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  background-color: #fff;
  border-color: rgba(25, 118, 210, 0.35) !important;
  box-shadow: 0 3px 10px rgba(25, 118, 210, 0.12);
}

.historial-control:hover:not(.v-btn--disabled) {
  background-color: rgba(25, 118, 210, 0.07);
  border-color: rgba(25, 118, 210, 0.6) !important;
}

.historial-control:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.18);
}

.historial-control.v-btn--disabled {
  background-color: rgba(0, 0, 0, 0.025) !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
  box-shadow: none;
}

/* Chip de tiempo transcurrido en el encabezado (sobre barra primary, ambos temas). */
.historial-header-tiempo {
  background-color: rgba(255, 255, 255, 0.18) !important;
  color: #fff !important;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.historial-timeline {
  grid-area: timeline;
  width: 100%;
  max-width: 100%;
  min-height: 520px;
  max-height: 68vh;
  max-height: min(68vh, 720px);
  overflow-x: auto;
  overflow-y: auto;
  overscroll-behavior-x: contain;
  overscroll-behavior-y: contain;
  scroll-snap-type: x proximity;
  background: linear-gradient(180deg, #f8faff 0%, #f4f7fb 100%);
  border: 1px solid rgba(25, 118, 210, 0.12);
  border-radius: 12px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 4px 18px rgba(30, 60, 90, 0.08);
  scrollbar-color: rgba(25, 118, 210, 0.4) transparent;
  scrollbar-width: thin;
}

.historial-timeline:focus-visible {
  outline: 2px solid rgba(25, 118, 210, 0.55);
  outline-offset: 2px;
}

.historial-timeline::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.historial-timeline::-webkit-scrollbar-thumb {
  background-color: rgba(25, 118, 210, 0.35);
  border: 2px solid transparent;
  border-radius: 999px;
  background-clip: padding-box;
}

.historial-track {
  position: relative;
  display: flex;
  align-items: stretch;
  gap: 28px;
  width: max-content;
  min-width: 100%;
  min-height: 520px;
  padding: 46px 24px;
}

.historial-track::before {
  content: "";
  position: absolute;
  top: calc(50% - 4px);
  left: 24px;
  right: 24px;
  height: 8px;
  background-color: rgba(84, 110, 122, 0.28);
  border-radius: 999px;
}

.historial-evento {
  --historial-evento-color: #546e7a;
  position: relative;
  display: grid;
  grid-template-rows: minmax(0, 1fr) 44px minmax(0, 1fr);
  flex: 0 0 320px;
  max-width: calc(100vw - 48px);
  scroll-snap-align: start;
}

.historial-evento::before {
  content: "";
  position: absolute;
  left: 25px;
  z-index: 1;
  width: 4px;
  height: 40px;
  background-color: var(--historial-evento-color);
  border-radius: 999px;
}

.historial-evento--arriba::before {
  bottom: 50%;
}

.historial-evento--abajo::before {
  top: 50%;
}

.historial-evento--repuestos {
  --historial-evento-color: #ff8f00;
}

.historial-evento--mano_obra {
  --historial-evento-color: #1565c0;
}

.historial-evento--terceros {
  --historial-evento-color: #6a1b9a;
}

.historial-evento--turno {
  --historial-evento-color: #00838f;
}

.historial-evento--orden {
  --historial-evento-color: #388e3c;
}

.historial-evento--peritaje {
  --historial-evento-color: #00897b;
}

.historial-evento--alerta {
  --historial-evento-color: #2196f3;
}

.historial-segmento {
  position: absolute;
  top: 50%;
  left: 28px;
  z-index: 1;
  width: calc(100% + 18px);
  height: 8px;
  background-color: var(--historial-evento-color);
  border-radius: 999px;
  transform: translateY(-50%);
  box-shadow: 0 2px 5px rgba(38, 50, 56, 0.2);
}

.historial-punto {
  grid-row: 2;
  align-self: center;
  justify-self: start;
  margin-left: 18px;
  z-index: 3;
  width: 20px;
  height: 20px;
  border: 4px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2), 0 2px 5px rgba(0, 0, 0, 0.18);
}

/* Familias de evento: replican los colores de categoría del tablero
   (repuestos amber, mano de obra blue, terceros purple) + peritaje teal y alerta info. */
.historial-punto--alerta {
  background-color: #2196f3;
}

.historial-punto--peritaje {
  background-color: #00897b;
}

.historial-punto--repuestos {
  background-color: #ff8f00;
}

.historial-punto--mano_obra {
  background-color: #1565c0;
}

.historial-punto--terceros {
  background-color: #6a1b9a;
}

.historial-punto--turno {
  background-color: #00838f;
}

.historial-punto--orden {
  background-color: #388e3c;
}

.historial-punto--neutro {
  background-color: #546e7a;
}

.historial-evento-card {
  max-height: 210px;
  overflow-y: auto;
  overflow-wrap: anywhere;
  background-color: rgba(255, 255, 255, 0.98);
  border-color: rgba(25, 118, 210, 0.18) !important;
  border-top: 3px solid #546e7a !important;
  border-radius: 10px !important;
  box-shadow: 0 5px 16px rgba(30, 60, 90, 0.1) !important;
}

.historial-evento-card--arriba {
  grid-row: 1;
  align-self: end;
  margin-bottom: 18px;
}

.historial-evento-card--abajo {
  grid-row: 3;
  align-self: start;
  margin-top: 18px;
}

.historial-lane-label {
  position: absolute;
  left: 24px;
  z-index: 2;
  padding: 4px 9px;
  color: rgba(38, 50, 56, 0.76);
  background-color: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(84, 110, 122, 0.18);
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.historial-lane-label--arriba {
  top: 10px;
}

.historial-lane-label--abajo {
  bottom: 10px;
}

.historial-evento-card--alerta {
  border-top-color: #2196f3 !important;
}

.historial-evento-card--peritaje {
  border-top-color: #00897b !important;
}

.historial-evento-card--repuestos {
  border-top-color: #ff8f00 !important;
}

.historial-evento-card--mano_obra {
  border-top-color: #1565c0 !important;
}

.historial-evento-card--terceros {
  border-top-color: #6a1b9a !important;
}

.historial-evento-card--turno {
  border-top-color: #00838f !important;
}

.historial-evento-card--orden {
  border-top-color: #388e3c !important;
}

.historial-evento-card--neutro {
  border-top-color: #546e7a !important;
}

.historial-evento-title {
  line-height: 1.35;
  margin-bottom: 8px;
}

/* Vuetify aplica margin-top negativo al subtítulo que sigue a un título de card
   (.v-card__title + .v-card__subtitle); con pb-1 la fecha se superponía al título. */
.historial-evento-title + .historial-evento-meta {
  margin-top: 0;
}

.historial-evento-meta {
  color: rgba(0, 0, 0, 0.58) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.historial-evento-subtitulo {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  line-height: 1.35;
  margin-bottom: 2px;
}

.historial-evento-body {
  padding-top: 16px !important;
}

.historial-detalle dt {
  color: rgba(0, 0, 0, 0.6);
}

/* Dark mode: superficies oscuras y contraste para toda la UI del historial. */
.theme--dark .historial-timeline {
  background: linear-gradient(180deg, #23262b 0%, #1a1c20 100%);
  border-color: rgba(255, 255, 255, 0.14);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 4px 18px rgba(0, 0, 0, 0.45);
  scrollbar-color: rgba(144, 202, 249, 0.4) transparent;
}

.theme--dark .historial-timeline:focus-visible {
  outline-color: rgba(144, 202, 249, 0.6);
}

.theme--dark .historial-timeline::-webkit-scrollbar-thumb {
  background-color: rgba(144, 202, 249, 0.35);
}

.theme--dark .historial-track::before {
  background-color: rgba(255, 255, 255, 0.22);
}

.theme--dark .historial-evento::before {
  background-color: var(--historial-evento-color);
}

.theme--dark .historial-segmento {
  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.5);
}

.theme--dark .historial-evento--repuestos {
  --historial-evento-color: #ffb300;
}

.theme--dark .historial-evento--mano_obra,
.theme--dark .historial-evento--alerta {
  --historial-evento-color: #64b5f6;
}

.theme--dark .historial-evento--terceros {
  --historial-evento-color: #ba68c8;
}

.theme--dark .historial-evento--turno {
  --historial-evento-color: #4dd0e1;
}

.theme--dark .historial-evento--orden {
  --historial-evento-color: #66bb6a;
}

.theme--dark .historial-evento--peritaje {
  --historial-evento-color: #26a69a;
}

.theme--dark .historial-evento--neutro {
  --historial-evento-color: #90a4ae;
}

.theme--dark .historial-punto {
  border-color: #1e1e1e;
  box-shadow: 0 0 0 2px rgba(144, 202, 249, 0.22), 0 2px 6px rgba(0, 0, 0, 0.5);
}

.theme--dark .historial-punto--alerta {
  background-color: #42a5f5;
}

.theme--dark .historial-punto--peritaje {
  background-color: #26a69a;
}

.theme--dark .historial-punto--repuestos {
  background-color: #ffa000;
}

.theme--dark .historial-punto--mano_obra {
  background-color: #42a5f5;
}

.theme--dark .historial-punto--terceros {
  background-color: #ab47bc;
}

.theme--dark .historial-punto--turno {
  background-color: #4dd0e1;
}

.theme--dark .historial-punto--orden {
  background-color: #66bb6a;
}

.theme--dark .historial-punto--neutro {
  background-color: #90a4ae;
}

.theme--dark .historial-evento-card {
  background-color: rgba(30, 30, 30, 0.98);
  border-color: rgba(255, 255, 255, 0.14) !important;
  box-shadow: 0 5px 16px rgba(0, 0, 0, 0.35) !important;
}

.theme--dark .historial-lane-label {
  color: rgba(255, 255, 255, 0.76);
  background-color: rgba(30, 30, 30, 0.9);
  border-color: rgba(255, 255, 255, 0.14);
}

.theme--dark .historial-evento-card--alerta {
  border-top-color: #64b5f6 !important;
}

.theme--dark .historial-evento-card--peritaje {
  border-top-color: #26a69a !important;
}

.theme--dark .historial-evento-card--repuestos {
  border-top-color: #ffb300 !important;
}

.theme--dark .historial-evento-card--mano_obra {
  border-top-color: #64b5f6 !important;
}

.theme--dark .historial-evento-card--terceros {
  border-top-color: #ba68c8 !important;
}

.theme--dark .historial-evento-card--turno {
  border-top-color: #4dd0e1 !important;
}

.theme--dark .historial-evento-card--orden {
  border-top-color: #66bb6a !important;
}

.theme--dark .historial-evento-card--neutro {
  border-top-color: #90a4ae !important;
}

.theme--dark .historial-evento-meta {
  color: rgba(255, 255, 255, 0.6) !important;
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .historial-evento-subtitulo {
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .historial-detalle dt {
  color: rgba(255, 255, 255, 0.62);
}

.theme--dark .historial-control {
  color: #90caf9 !important;
  background-color: #1e1e1e;
  border-color: rgba(144, 202, 249, 0.35) !important;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.35);
}

.theme--dark .historial-control:hover:not(.v-btn--disabled) {
  background-color: rgba(144, 202, 249, 0.12);
  border-color: rgba(144, 202, 249, 0.6) !important;
}

.theme--dark .historial-control:focus-visible {
  box-shadow: 0 0 0 3px rgba(144, 202, 249, 0.25);
}

.theme--dark .historial-control.v-btn--disabled {
  color: rgba(255, 255, 255, 0.38) !important;
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-color: rgba(255, 255, 255, 0.14) !important;
  box-shadow: none;
}

.conceptos-detalle {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding-top: 8px;
}

.peritaje-viewer-container {
  min-height: 400px;
}

.peritaje-pdf-frame {
  display: block;
  width: 100%;
  min-height: 500px;
  height: 70vh;
  border: 0;
}

.peritaje-image {
  min-height: 400px;
}

.item-fila {
  min-height: 26px;
}

.categoria-btn {
  transition: all 0.2s ease;
  font-weight: 500;
  letter-spacing: 0.5px;
}
.categoria-btn--active {
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.35);
  transform: translateY(-1px);
}

@media (max-width: 959px) {
  .reparaciones-mobile-tabs {
    display: flex;
  }

  .reparaciones-toolbar-actions {
    justify-content: flex-start !important;
  }
}

@media (max-width: 599px) {
  .historial-content {
    padding: 12px !important;
  }

  .historial-navigation {
    gap: 4px 6px;
  }

  .historial-control {
    width: 40px !important;
    min-width: 40px !important;
    height: 40px !important;
  }

  .historial-timeline,
  .historial-track {
    min-height: 480px;
  }

  .historial-track {
    gap: 18px;
    padding: 42px 14px;
  }

  .historial-track::before {
    left: 14px;
    right: 14px;
  }

  .historial-evento {
    flex-basis: 280px;
    max-width: calc(100vw - 124px);
  }

  .historial-evento-card {
    max-height: 190px;
  }

  .historial-lane-label {
    left: 14px;
    max-width: calc(100vw - 152px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .reparaciones-kanban {
    height: auto;
    min-width: 0;
  }

  .reparaciones-toolbar,
  .reparaciones-board-row,
  .reparaciones-finalizados-row {
    margin-left: 0;
    margin-right: 0;
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .reparaciones-toolbar-actions .v-btn {
    flex: 1 1 100%;
    min-height: 44px;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .reparaciones-board-col {
    height: auto;
    padding-left: 0;
    padding-right: 0;
  }

  .reparaciones-board {
    height: auto;
  }

  .reparaciones-finalizados-search {
    max-width: none;
  }

  .reparaciones-dialog-title {
    align-items: flex-start;
    font-size: 1.1rem !important;
    line-height: 1.35;
  }

  .reparaciones-dialog-title--closable {
    align-items: center;
  }

  .reparaciones-dialog-actions .v-btn {
    flex: 1 1 auto;
    min-height: 44px;
  }

  .reparaciones-dialog-title .v-btn.v-btn--icon,
  .reparaciones-touch-table .v-btn.v-btn--icon {
    width: 44px;
    min-width: 44px;
    height: 44px;
  }

  .reparaciones-touch-table .v-btn:not(.v-btn--icon) {
    min-height: 44px;
  }
}
</style>
